/**
 * web/v2/src/widgets/OsintResearch.jsx — Sovereign "Find By" OSINT Swiss Army Knife.
 *
 * Features a top-level toggle between:
 *   - ✨ Simple Mode: Clean, family-friendly multi-clue batch compiler (Name,
 *     Username, Email, Phone, Address/City/State, Website, and optional local
 *     Photo/Media attachment) with one-click "Find Potential Matches" and
 *     plain-English cross-clue correlation.
 *   - 🛠️ Pro Mode: 11 specialized entity modes, Multi-Hop Pivot Chips &
 *     Investigation Trail, Case Board Dossier exporter, Local File Lab, and
 *     Fissal OSINT Co-Pilot with autonomous tool calling.
 */

import { createSignal, createMemo, createEffect, For, Show, onCleanup } from 'solid-js';
import wm from '../lib/wmStore.js';
import './OsintResearch.css';

const DEFAULT_MODES = [
  {
    id: 'auto',
    label: '✨ Auto-Detect',
    shortLabel: '✨ Auto',
    placeholder: 'Username, email, domain, IP, company/person, ETH/BTC, CVE, phone, MAC, or ICAO24…',
    description: 'Automatically classifies any identifier and dispatches all matching sovereign & public-record collectors.',
    examples: ['@torvalds', 'cloudflare.com', '1.1.1.1', 'Cloudflare Inc', 'vitalik.eth', 'CVE-2024-3094'],
  },
  {
    id: 'username',
    label: '👤 Username',
    shortLabel: '👤 Handle',
    placeholder: 'username or @handle',
    description: 'Sweeps local Redfur DB + 24 developer, commit-patch email mining, social, fediverse, wiki, and proof APIs.',
    examples: ['torvalds', 'echotiger'],
  },
  {
    id: 'email',
    label: '✉️ Email',
    shortLabel: '✉️ Email',
    placeholder: 'user@example.org',
    description: 'Local alias/provider anatomy, SHA-256/MD5 Gravatar, DNS MX/SPF/DMARC, WebFinger, OpenPGP, GitHub & npm.',
    examples: ['security@example.org'],
  },
  {
    id: 'domain',
    label: '🌐 Domain',
    shortLabel: '🌐 Domain',
    placeholder: 'example.org',
    description: 'Local DNS (A/AAAA/MX/NS/TXT/SOA/CAA/DMARC), live TLS X.509 socket, HTTP headers, security.txt, RDAP, crt.sh, urlscan & Wayback.',
    examples: ['cloudflare.com', 'example.org'],
  },
  {
    id: 'ip',
    label: '📡 IP Address',
    shortLabel: '📡 IP',
    placeholder: '1.1.1.1 or 2606:4700:4700::1111',
    description: 'Local reverse DNS (PTR), RIR RDAP netblock, RIPE Stat BGP prefix, geo/ASN, Shodan InternetDB passive ports & live TLS.',
    examples: ['1.1.1.1', '8.8.8.8'],
  },
  {
    id: 'org',
    label: '🏛️ Public Records',
    shortLabel: '🏛️ Records',
    placeholder: 'Company, Legal Entity, Academic Author, or Location…',
    description: 'Global corporate filings (GLEIF LEI), academic researchers/ORCID & ROR institutions (OpenAlex), Crossref DOIs, Wikidata, OpenStreetMap & Archive.org.',
    examples: ['Cloudflare Inc', 'Mozilla Foundation'],
  },
  {
    id: 'crypto',
    label: '⛓️ Crypto / Web3',
    shortLabel: '⛓️ Web3',
    placeholder: '0x..., name.eth, bc1..., or 64-hex txid',
    description: 'Ethereum Mainnet JSON-RPC balance/bytecode, ENS reverse social resolution, and Bitcoin Mempool address/tx inspection.',
    examples: ['vitalik.eth', '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa'],
  },
  {
    id: 'cve',
    label: '🛡️ CVE / Threat',
    shortLabel: '🛡️ CVE',
    placeholder: 'CVE-2024-3094 or GHSA-xxxx-xxxx-xxxx',
    description: 'Official MITRE CVE AWG records (CVSS severity, affected vendors) and Google OSV.dev open-source package advisories.',
    examples: ['CVE-2024-3094', 'CVE-2021-44228'],
  },
  {
    id: 'phone',
    label: '📞 Phone / E.164',
    shortLabel: '📞 Phone',
    placeholder: '+1-415-555-0199',
    description: '100% local ITU-T E.164 country numbering plan & North American (NANPA) area-code geography/timezone inspector.',
    examples: ['+1-415-555-0199', '+44 20 7946 0921'],
  },
  {
    id: 'mac',
    label: '🔧 MAC / OUI',
    shortLabel: '🔧 MAC',
    placeholder: '24:6f:28:aa:bb:cc',
    description: 'Local IEEE 802 UAA/LAA randomized bit inspection + built-in Sovereign Homelab OUI dictionary.',
    examples: ['24:6f:28:aa:bb:cc', 'bc:24:11:00:11:22'],
  },
  {
    id: 'aircraft',
    label: '✈️ Aircraft',
    shortLabel: '✈️ ICAO24',
    placeholder: 'a273f5 (6-char hex)',
    description: 'Live ADS-B transponder telemetry and 24-hour flight history via OpenSky.',
    examples: ['a273f5', 'a1b2c3'],
  },
];

const DEFAULT_FILTERS = [
  { id: 'all', label: 'All Evidence' },
  { id: 'local', label: 'Sovereign / Local' },
  { id: 'profile', label: 'Profiles & Social' },
  { id: 'code', label: 'Code & Commits' },
  { id: 'dns', label: 'DNS & Network' },
  { id: 'certificate', label: 'TLS & Certs' },
  { id: 'security', label: 'Security & CVEs' },
  { id: 'registration', label: 'Registries & Archives' },
  { id: 'hardware', label: 'Hardware & ADS-B' },
];

const TYPE_GROUP_MAP = {
  local: 'local',
  profile: 'profile',
  social: 'profile',
  code: 'code',
  dns: 'dns',
  network: 'dns',
  certificate: 'certificate',
  security: 'security',
  registration: 'registration',
  archive: 'registration',
  hardware: 'hardware',
  flight: 'hardware',
};

const TYPE_ICONS = {
  local: '🛡️',
  profile: '👤',
  social: '💬',
  code: '📦',
  dns: '🌐',
  network: '📡',
  certificate: '🔐',
  security: '🔑',
  registration: '🏛️',
  archive: '🕰️',
  hardware: '🔧',
  flight: '✈️',
};

const CONFIDENCE_LABELS = {
  'verified-local': { label: '⚡ Sovereign Local', cls: 'osint-Research__conf--local' },
  'direct-api': { label: '🔌 Direct API', cls: 'osint-Research__conf--api' },
  'public-record': { label: '🏛️ Public Record', cls: 'osint-Research__conf--record' },
};

function clientDetectMode(rawInput) {
  const raw = String(rawInput || '').trim();
  if (!raw) return null;
  if (/^(?:CVE-\d{4}-\d{4,8}|GHSA-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{4})$/i.test(raw)) return 'cve';
  if (/^(?:0x[0-9a-fA-F]{40}|[a-zA-Z0-9-]{2,64}\.eth|(?:[13][a-km-zA-HJ-NP-Z1-9]{25,34}|bc1[ac-hj-np-z02-9]{11,71}))$/i.test(raw)) return 'crypto';
  if (/^(?:icao(?:24)?|hex):[0-9a-f]{6}$/i.test(raw)) return 'aircraft';
  if (/^[^\s@]+@[^\s@]+\.[a-z]{2,63}$/i.test(raw)) return 'email';
  if (/^(?:\d{1,3}\.){3}\d{1,3}$/.test(raw) || (raw.includes(':') && /^[0-9a-f:]+$/i.test(raw) && raw.split(':').length !== 6 && raw.split(':').length !== 3)) {
    return 'ip';
  }
  if (/^([0-9a-f]{2}[:-]){5}[0-9a-f]{2}$/i.test(raw) ||
      /^([0-9a-f]{2}[:-]){2}[0-9a-f]{2}$/i.test(raw) ||
      /^[0-9a-f]{4}\.[0-9a-f]{4}\.[0-9a-f]{4}$/i.test(raw)) {
    return 'mac';
  }
  const digits = raw.replace(/[^\d]/g, '');
  if (/^\+?\d[\d\s().-]{6,18}\d$/.test(raw) && digits.length >= 10 && digits.length <= 15 && (raw.startsWith('+') || /[\s().-]/.test(raw))) {
    return 'phone';
  }
  if (!raw.startsWith('@')) {
    if (/^https?:\/\//i.test(raw)) return 'domain';
    const stripped = raw.replace(/\/.*$/, '').replace(/\.$/, '');
    if (/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i.test(stripped)) {
      return 'domain';
    }
  }
  if (raw.includes(' ')) return 'org';
  return 'username';
}

function bufferToHex(buf) {
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

async function extractMediaFileClues(file) {
  const buf = await file.arrayBuffer();
  const [sha256Buf, sha1Buf] = await Promise.all([
    crypto.subtle.digest('SHA-256', buf),
    crypto.subtle.digest('SHA-1', buf),
  ]);
  const sha256 = bufferToHex(sha256Buf);
  const sha1 = bufferToHex(sha1Buf);

  const bytes = new Uint8Array(buf.slice(0, Math.min(buf.byteLength, 96 * 1024)));
  const magicHex = Array.from(bytes.slice(0, 8)).map(b => b.toString(16).padStart(2, '0').toUpperCase()).join(' ');

  let ascii = '';
  for (let i = 0; i < bytes.length; i++) {
    const c = bytes[i];
    ascii += (c >= 32 && c <= 126) ? String.fromCharCode(c) : ' ';
  }

  const urls = Array.from(new Set(ascii.match(/https?:\/\/[a-zA-Z0-9._~:/?#[\]@!$&'()*+,;=-]{6,120}/g) || [])).slice(0, 10);
  const emails = Array.from(new Set(ascii.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,16}/g) || [])).slice(0, 10);
  const exifHints = Array.from(new Set(
    ascii.match(/\b(?:Adobe|Photoshop|Lightroom|GIMP|Canon|Nikon|Sony|Apple|iPhone|Samsung|Pixel|Exif|Creator|Producer|Author|macOS|Windows|Linux)[a-zA-Z0-9 ._/-]{0,36}/g) || [],
  )).map(s => s.trim()).filter(s => s.length >= 4).slice(0, 12);

  let dimensions = null;
  if (file.type.startsWith('image/')) {
    dimensions = await new Promise(resolve => {
      const img = new Image();
      const objUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objUrl);
        resolve(`${img.naturalWidth} × ${img.naturalHeight} px`);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objUrl);
        resolve(null);
      };
      img.src = objUrl;
    });
  }

  return {
    name: file.name,
    fileName: file.name,
    sizeBytes: file.size,
    mimeType: file.type || 'application/octet-stream',
    lastModified: file.lastModified ? new Date(file.lastModified).toISOString() : null,
    magicHex,
    dimensions,
    sha256,
    sha1,
    urls,
    emails,
    exifHints,
  };
}

export default function OsintResearch(props) {
  const cfg = () => props.settings || props.instance?.config || {};

  // Top-level Simple Mode vs Pro Mode toggle
  const initialUiMode = () => {
    const c = cfg();
    if (c.uiMode === 'simple' || c.uiMode === 'pro') return c.uiMode;
    if (c.defaultUiMode === 'simple' || c.defaultUiMode === 'pro') return c.defaultUiMode;
    try {
      const saved = localStorage.getItem('redfur_osint_ui_mode');
      if (saved === 'simple' || saved === 'pro') return saved;
    } catch { /* ignore */ }
    return 'simple';
  };
  const [uiMode, setUiModeState] = createSignal(initialUiMode());
  function setUiMode(mode) {
    setUiModeState(mode);
    setError(null);
    try { localStorage.setItem('redfur_osint_ui_mode', mode); } catch { /* ignore */ }
  }

  // Simple Mode Batched Form Signals (8 fields + optional media attachment)
  const [simpleName, setSimpleName] = createSignal('');
  const [simpleAge, setSimpleAge] = createSignal('');
  const [simpleLocation, setSimpleLocation] = createSignal('');
  const [simpleUsername, setSimpleUsername] = createSignal('');
  const [simpleEmail, setSimpleEmail] = createSignal('');
  const [simplePhone, setSimplePhone] = createSignal('');
  const [simpleWebsite, setSimpleWebsite] = createSignal('');
  const [simpleExtra, setSimpleExtra] = createSignal('');
  const [simpleMedia, setSimpleMedia] = createSignal(null);
  const [simpleMediaBusy, setSimpleMediaBusy] = createSignal(false);
  const [simpleResult, setSimpleResult] = createSignal(null);
  const [simpleFilter, setSimpleFilter] = createSignal('all'); // 'all' | 'strong' | 'profile' | 'location' | 'records'
  const [hasSearchedSimple, setHasSearchedSimple] = createSignal(false);
  const [autoRefreshing, setAutoRefreshing] = createSignal(false);

  // "Port This Data to Widget" & Active Dashboard Target state
  const [portedBanner, setPortedBanner] = createSignal(null);
  const [portedFieldKeys, setPortedFieldKeys] = createSignal([]);
  const [targetedGroupId, setTargetedGroupId] = createSignal(null);
  const [expandedGroupIds, setExpandedGroupIds] = createSignal({});

  // Subject Dossier Pop-Up Modal + Embedded Subject Fissal Chat state
  const [selectedSubject, setSelectedSubject] = createSignal(null);
  const [subjectChatMessages, setSubjectChatMessages] = createSignal([]);
  const [subjectChatInput, setSubjectChatInput] = createSignal('');
  const [subjectChatStreaming, setSubjectChatStreaming] = createSignal(false);
  const [subjectToolsUsed, setSubjectToolsUsed] = createSignal([]);

  // Pro Mode Signals
  const [selectedMode, setSelectedMode] = createSignal(cfg().selectedMode || cfg().defaultMode || 'auto');
  const [activeFilter, setActiveFilter] = createSignal(cfg().activeFilter || cfg().defaultFilter || 'all');
  const [activeView, setActiveView] = createSignal(cfg().activeView || 'feed'); // 'feed' | 'caseboard' | 'filelab' | 'fissal'
  const [targetInput, setTargetInput] = createSignal('');
  const [filterQuery, setFilterQuery] = createSignal('');
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal(null);
  const [result, setResult] = createSignal(null);
  const [copiedId, setCopiedId] = createSignal(null);

  createEffect(() => {
    const wid = props.widget?.id;
    const um = uiMode();
    const sm = selectedMode();
    const af = activeFilter();
    const av = activeView();
    if (!wid) return;
    const cur = props.widget?.settings || {};
    if (
      cur.uiMode !== um ||
      cur.selectedMode !== sm ||
      cur.activeFilter !== af ||
      cur.activeView !== av
    ) {
      wm.setSettings(wid, {
        ...cur,
        uiMode: um,
        selectedMode: sm,
        activeFilter: af,
        activeView: av,
      });
    }
  });

  // Multi-hop Investigation Trail (History of searches in this session)
  const [trail, setTrail] = createSignal([]);

  // Case Board (Pinned Evidence Dossier across searches)
  const [pinnedItems, setPinnedItems] = createSignal([]);
  const [caseNotes, setCaseNotes] = createSignal('');

  // Local File Lab state
  const [fileReport, setFileReport] = createSignal(null);
  const [fileAnalyzing, setFileAnalyzing] = createSignal(false);

  // Fissal Co-Pilot state
  const [fissalPrompt, setFissalPrompt] = createSignal('');
  const [fissalResponse, setFissalResponse] = createSignal('');
  const [fissalStreaming, setFissalStreaming] = createSignal(false);
  const [fissalError, setFissalError] = createSignal(null);
  const [fissalToolsUsed, setFissalToolsUsed] = createSignal([]);
  const [deepPullBusy, setDeepPullBusy] = createSignal(false);
  const [showFrameworkPanel, setShowFrameworkPanel] = createSignal(false);
  let fissalAbort = null;
  let subjectChatAbort = null;
  let autoRefreshTimer = null;

  onCleanup(() => {
    if (fissalAbort) fissalAbort.abort();
    if (subjectChatAbort) subjectChatAbort.abort();
    if (autoRefreshTimer) clearTimeout(autoRefreshTimer);
  });

  createEffect(() => {
    if (cfg().defaultMode) setSelectedMode(cfg().defaultMode);
    if (cfg().defaultFilter) setActiveFilter(cfg().defaultFilter);
    if (cfg().defaultUiMode === 'simple' || cfg().defaultUiMode === 'pro') {
      setUiModeState(cfg().defaultUiMode);
    }
  });

  const modes = createMemo(() => {
    const list = props.data?.modes;
    return Array.isArray(list) && list.length > 0 ? list : DEFAULT_MODES;
  });

  const filters = createMemo(() => {
    const list = props.data?.filters;
    return Array.isArray(list) && list.length > 0 ? list : DEFAULT_FILTERS;
  });

  const activeModeObj = createMemo(() =>
    modes().find(m => m.id === selectedMode()) || modes()[0],
  );

  const detectedModeHint = createMemo(() => {
    if (selectedMode() !== 'auto') return null;
    const det = clientDetectMode(targetInput());
    if (!det) return null;
    return modes().find(m => m.id === det) || null;
  });

  const findings = createMemo(() => result()?.findings || []);
  const pivots = createMemo(() => result()?.pivots || []);

  const filteredFindings = createMemo(() => {
    const fId = activeFilter();
    const q = filterQuery().trim().toLowerCase();
    return findings().filter(item => {
      if (fId !== 'all') {
        const group = TYPE_GROUP_MAP[item.type] || item.type;
        if (group !== fId && item.type !== fId) return false;
      }
      if (!q) return true;
      const hay = `${item.title || ''} ${item.summary || ''} ${item.source || ''} ${(item.meta || []).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  });

  const categoryCounts = createMemo(() => {
    const counts = { all: findings().length };
    for (const item of findings()) {
      const group = TYPE_GROUP_MAP[item.type] || item.type;
      counts[group] = (counts[group] || 0) + 1;
    }
    return counts;
  });

  // Client-side instant re-scoring so when additional fields (e.g. State "NC" or Age "40-50") are typed,
  // existing matches immediately re-rank and crown the closest "🏆 Best Match" while backend refresh runs
  const liveRankedSimpleMatches = createMemo(() => {
    const rawList = simpleResult()?.matches || [];
    if (rawList.length === 0) return [];

    const locInput = simpleLocation().trim().toLowerCase();
    const ageInput = simpleAge().trim().toLowerCase();
    const nameInput = simpleName().trim().toLowerCase();
    const handleInput = simpleUsername().trim().replace(/^@+/, '').toLowerCase();
    const emailInput = simpleEmail().trim().toLowerCase();
    const extraInput = simpleExtra().trim().toLowerCase();

    let targetAge = null;
    let minAge = null;
    let maxAge = null;
    if (ageInput) {
      const rm = ageInput.match(/(\d{1,3})\s*(?:[-–—]|to)\s*(\d{1,3})/);
      const ym = ageInput.match(/\b(19[2-9]\d|20[0-2]\d)\b/);
      const sm = ageInput.match(/\b(\d{1,3})\b/);
      const curYr = new Date().getFullYear();
      if (ym) {
        targetAge = curYr - Number(ym[1]);
        minAge = targetAge - 2;
        maxAge = targetAge + 2;
      } else if (rm) {
        minAge = Math.min(Number(rm[1]), Number(rm[2]));
        maxAge = Math.max(Number(rm[1]), Number(rm[2]));
        targetAge = Math.round((minAge + maxAge) / 2);
      } else if (sm && Number(sm[1]) >= 10 && Number(sm[1]) <= 115) {
        targetAge = Number(sm[1]);
        minAge = targetAge - 3;
        maxAge = targetAge + 3;
      }
    }

    const rescored = rawList.map(m => {
      let bonus = 0;
      const extraReasons = [...(m.corroborations || [])];
      const hay = `${m.title || ''} ${m.summary || ''} ${(m.meta || []).join(' ')} ${m.extractedFields?.location || ''} ${m.extractedFields?.name || ''}`.toLowerCase();

      if (locInput.length >= 2) {
        const isTwoLetter = locInput.length === 2;
        const matched = isTwoLetter
          ? new RegExp(`(?:\\b|US-)${locInput}\\b`, 'i').test(`${m.title || ''} ${m.summary || ''} ${m.extractedFields?.location || ''}`)
          : hay.includes(locInput);
        if (matched) {
          bonus += 6;
          const rText = `Matches location/state (${locInput.toUpperCase()})`;
          if (!extraReasons.some(r => r.toLowerCase().includes('location'))) extraReasons.push(rText);
        }
      }

      if (targetAge !== null && m.extractedFields?.estimatedAge) {
        const candAge = Number(m.extractedFields.estimatedAge);
        if (candAge >= minAge && candAge <= maxAge) {
          bonus += 5;
        } else {
          const dist = candAge < minAge ? minAge - candAge : candAge - maxAge;
          if (dist <= 5) bonus += 3;
          else if (dist <= 10) bonus += 1;
          else bonus -= 2;
        }
      }

      if (nameInput.length >= 2 && hay.includes(nameInput)) bonus += 2;
      if (handleInput.length >= 2 && hay.includes(handleInput)) bonus += 3;
      if (emailInput.length >= 3 && hay.includes(emailInput)) bonus += 4;
      if (extraInput.length >= 2 && hay.includes(extraInput)) bonus += 3;

      return {
        ...m,
        liveScore: (m.score || 1) + bonus,
        corroborations: extraReasons,
      };
    });

    rescored.sort((a, b) => b.liveScore - a.liveScore);

    return rescored.map((m, idx) => ({
      ...m,
      isBestMatch: idx === 0,
      matchTier: idx === 0 ? 'best' : m.matchTier === 'best' ? 'strong' : m.matchTier,
      matchBadge: idx === 0
        ? '🏆 Best Match'
        : m.matchTier === 'best' || m.matchTier === 'strong'
          ? '🔥 Strong Multi-Clue Match'
          : m.matchBadge,
    }));
  });

  const filteredSimpleMatches = createMemo(() => {
    const f = simpleFilter();
    return liveRankedSimpleMatches().filter(m => {
      if (f === 'all') return true;
      if (f === 'strong') return m.isBestMatch || m.matchTier === 'best' || m.matchTier === 'strong' || m.matchTier === 'likely';
      if (f === 'profile') return m.type === 'profile' || m.type === 'social' || m.type === 'code';
      if (f === 'location') return m.type === 'local' || m.type === 'network' || Boolean(m.extractedFields?.location);
      if (f === 'records') return m.type === 'registration' || m.type === 'archive' || m.type === 'dns' || m.type === 'certificate' || m.type === 'security';
      return true;
    });
  });

  // Live-ranked grouped Potential Matches (each group is a distinct candidate person/entity; exact matches ALWAYS show first)
  const livePotentialMatchGroups = createMemo(() => {
    const rawGroups = simpleResult()?.potentialMatches || [];
    if (rawGroups.length === 0) return [];

    const locInput = simpleLocation().trim().toLowerCase();
    const ageInput = simpleAge().trim().toLowerCase();
    const nameInput = simpleName().trim().toLowerCase();
    const handleInput = simpleUsername().trim().replace(/^@+/, '').toLowerCase();
    const emailInput = simpleEmail().trim().toLowerCase();
    const extraInput = simpleExtra().trim().toLowerCase();

    const rescored = rawGroups.map(g => {
      let bonus = 0;
      const cf = g.consolidatedFacts || {};
      const hay = [
        g.name,
        g.subtitle,
        g.simplifiedSummary,
        ...(cf.addresses || []),
        ...(cf.phones || []),
        ...(cf.handles || []),
        ...(cf.relatives || []),
        ...(cf.education || []),
      ].filter(Boolean).join(' ').toLowerCase();

      if (locInput.length >= 2 && hay.includes(locInput)) bonus += 6;
      if (nameInput.length >= 2 && hay.includes(nameInput)) bonus += 4;
      if (handleInput.length >= 2 && hay.includes(handleInput)) bonus += 5;
      if (emailInput.length >= 3 && hay.includes(emailInput)) bonus += 5;
      if (extraInput.length >= 2 && hay.includes(extraInput)) bonus += 4;
      if (ageInput.length >= 2 && hay.includes(ageInput)) bonus += 4;

      return {
        ...g,
        liveScore: (g.score || 1) + bonus,
      };
    });

    // Preserve exactMatchRank first so exact first+last name or business matches ALWAYS stay ahead of partial/middle-name matches
    rescored.sort((a, b) => {
      const rankDiff = (b.exactMatchRank ?? 1) - (a.exactMatchRank ?? 1);
      if (rankDiff !== 0) return rankDiff;
      return b.liveScore - a.liveScore;
    });

    return rescored.map((g, idx) => ({
      ...g,
      isBestMatch: idx === 0,
      matchTier: idx === 0 ? 'best' : g.isExactMatch ? 'strong' : g.matchTier === 'best' ? 'strong' : g.matchTier,
      matchBadge: idx === 0
        ? (g.isExactMatch ? '🏆 #1 Exact Match' : '🏆 #1 Best Potential Match')
        : g.isExactMatch
          ? '🎯 Exact Name Match'
          : g.matchTier === 'best' || g.matchTier === 'strong'
            ? '🔥 Strong Potential Match'
            : g.matchBadge,
    }));
  });

  const filteredPotentialMatchGroups = createMemo(() => {
    const f = simpleFilter();
    const groups = livePotentialMatchGroups();
    if (f === 'all') return groups;
    return groups
      .map(g => {
        const matchingEntries = (g.entries || []).filter(m => {
          if (f === 'strong') return m.isBestMatch || m.matchTier === 'best' || m.matchTier === 'strong' || m.matchTier === 'likely';
          if (f === 'profile') return m.type === 'profile' || m.type === 'social' || m.type === 'code';
          if (f === 'location') return m.type === 'local' || m.type === 'network' || Boolean(m.extractedFields?.location || m.extractedFields?.address);
          if (f === 'records') return m.type === 'registration' || m.type === 'archive' || m.type === 'dns' || m.type === 'certificate' || m.type === 'security';
          return true;
        });
        if (f === 'strong' && (g.isBestMatch || g.isExactMatch || g.matchTier === 'best' || g.matchTier === 'strong')) {
          return g;
        }
        if (matchingEntries.length === 0) return null;
        return { ...g, entries: matchingEntries };
      })
      .filter(Boolean);
  });

  const activeTargetedGroup = createMemo(() => {
    const groups = livePotentialMatchGroups();
    const tid = targetedGroupId();
    if (tid) {
      const found = groups.find(g => g.id === tid);
      if (found) return found;
    }
    return groups[0] || null;
  });

  // Dynamic Subject Profile Dossier that reflects the currently targeted Potential Match group!
  const activeSubjectProfile = createMemo(() => {
    const baseSp = simpleResult()?.subjectProfile;
    const tg = activeTargetedGroup();
    if (!baseSp) return null;
    if (!tg || !tg.consolidatedFacts) return baseSp;

    const cf = tg.consolidatedFacts;
    const words = String(cf.fullName || tg.name || baseSp.fullName || '').split(/\s+/).filter(Boolean);
    const nameParts = words.length >= 2
      ? {
          fullName: cf.fullName || tg.name,
          firstName: words[0],
          middleName: words.length >= 3 ? words.slice(1, -1).join(' ') : '',
          lastName: words[words.length - 1],
          initialsFormatted: `${words.map(w => w[0].toUpperCase()).join('.')}.`,
        }
      : baseSp.nameParts;

    return {
      ...baseSp,
      fullName: cf.fullName || tg.name || baseSp.fullName,
      nameParts,
      avatar: tg.avatar || baseSp.avatar,
      age: cf.age || baseSp.age,
      birthYear: cf.birthYear || baseSp.birthYear,
      phones: cf.phones?.length > 0 ? cf.phones : baseSp.phones,
      addresses: cf.addresses?.length > 0 ? cf.addresses : baseSp.addresses,
      emails: cf.emails?.length > 0 ? cf.emails : baseSp.emails,
      relatives: cf.relatives?.length > 0 ? cf.relatives : (baseSp.relatives || []),
      education: cf.education?.length > 0 ? cf.education : (baseSp.education || []),
      voterRecords: cf.voterRecords?.length > 0 ? cf.voterRecords : (baseSp.voterRecords || []),
    };
  });

  function toggleGroupExpanded(groupId, e) {
    if (e) e.stopPropagation();
    setExpandedGroupIds(prev => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  }

  /**
   * Clicking on any grouped Potential Match (or any individual entry inside it)
   * locks the dashboard and widget onto that specific candidate without mutating
   * the user's Simple Mode search inputs or causing the match list to jump.
   */
  function selectPotentialMatchAsTarget(group, specificEntry = null) {
    if (!group) return;
    setTargetedGroupId(group.id);

    const spec = group.targetSpec || {};
    const ef = specificEntry?.extractedFields || {};
    const cf = group.consolidatedFacts || {};

    const nameVal = ef.name || spec.name || cf.fullName || '';
    const ageVal = ef.age || spec.age || cf.age || '';
    const locVal = ef.address || ef.location || spec.location || (cf.addresses && cf.addresses[0]) || '';
    const handleVal = ef.username || spec.username || (cf.handles && cf.handles[0]) || '';
    const emailVal = ef.email || spec.email || (cf.emails && cf.emails[0]) || '';
    const phoneVal = ef.phone || spec.phone || (cf.phones && cf.phones[0]) || '';

    const summaryBits = [
      locVal ? `📍 ${locVal}` : null,
      phoneVal ? `📞 ${phoneVal}` : null,
      ageVal && group.entityKind !== 'business' ? `🎂 ${ageVal}` : null,
      handleVal ? `🏷️ @${String(handleVal).replace(/^@+/, '')}` : null,
    ].filter(Boolean);

    // Set Pro Mode target & update Pro Mode feed to focus on this candidate's entries
    const proTarget = handleVal || emailVal || nameVal || phoneVal || group.name;
    const proMode = handleVal ? 'username' : emailVal ? 'email' : phoneVal ? 'phone' : 'org';
    if (proTarget) {
      setTargetInput(proTarget);
      setSelectedMode(proMode);
    }

    if (Array.isArray(group.entries) && group.entries.length > 0) {
      setResult(prev => ({
        ...(prev || {}),
        ok: true,
        mode: proMode,
        target: group.name,
        searchedAt: new Date().toISOString(),
        sourcesChecked: group.entries.length,
        profileCard: {
          kind: '🎯 Active Dashboard Target · Potential Match Dossier',
          title: group.name,
          subtitle: group.subtitle,
          avatar: group.avatar || prev?.profileCard?.avatar || null,
          summary: group.simplifiedSummary || prev?.profileCard?.summary || '',
          badges: [group.matchBadge, `${group.entries.length} records`, ...(cf.handles || []).slice(0, 3).map(h => `@${h}`)],
        },
        findings: group.entries,
      }));
    }

    // Persist active target in dashboard window manager settings
    const wid = props.widget?.id;
    if (wid) {
      const cur = props.widget?.settings || {};
      wm.setSettings(wid, {
        ...cur,
        activeTargetName: group.name,
        activeTargetId: group.id,
        activeTargetSpec: spec,
      });
    }

    setPortedBanner(
      `🎯 Dashboard Target Locked: ${group.name}${summaryBits.length ? ` (${summaryBits.join(' · ')})` : ''}`,
    );
  }

  /**
   * Explicitly loads a grouped Potential Match's details into the 8 Simple Mode search boxes
   * (clearing any blank fields so two candidates' clues never mix).
   */
  function fillSimpleFormFromGroup(group, specificEntry = null) {
    if (!group) return;
    selectPotentialMatchAsTarget(group, specificEntry);

    const spec = group.targetSpec || {};
    const ef = specificEntry?.extractedFields || {};
    const cf = group.consolidatedFacts || {};
    const keys = [];

    const nameVal = ef.name || spec.name || cf.fullName || '';
    setSimpleName(nameVal && !nameVal.startsWith('@') && !nameVal.startsWith('+') ? nameVal : '');
    if (nameVal) keys.push('name');

    const ageVal = group.entityKind === 'business' ? '' : (ef.age || spec.age || cf.age || '');
    setSimpleAge(ageVal ? String(ageVal) : '');
    if (ageVal) keys.push('age');

    const locVal = ef.address || ef.location || spec.location || (cf.addresses && cf.addresses[0]) || '';
    setSimpleLocation(locVal || '');
    if (locVal) keys.push('location');

    const handleVal = ef.username || spec.username || (cf.handles && cf.handles[0]) || '';
    const cleanH = handleVal ? String(handleVal).replace(/^@+/, '') : '';
    setSimpleUsername(cleanH);
    if (cleanH) keys.push('username');

    const emailVal = ef.email || spec.email || (cf.emails && cf.emails[0]) || '';
    setSimpleEmail(emailVal ? emailVal.toLowerCase() : '');
    if (emailVal) keys.push('email');

    const phoneVal = ef.phone || spec.phone || (cf.phones && cf.phones[0]) || '';
    setSimplePhone(phoneVal || '');
    if (phoneVal) keys.push('phone');

    const webVal = ef.website || spec.website || (cf.websites && cf.websites[0]) || '';
    setSimpleWebsite(webVal || '');
    if (webVal) keys.push('website');

    const extraVal = spec.extraClues || '';
    setSimpleExtra(extraVal || '');
    if (extraVal) keys.push('extra');

    setPortedFieldKeys(keys);
    setPortedBanner(`📥 Loaded ${group.name}'s details into the search boxes above.`);
    setTimeout(() => setPortedFieldKeys([]), 3200);
  }

  /**
   * Builds a synthesized subject dossier object from a grouped Potential Match card
   * so opening the "Ask Fissal" modal from a card includes ALL consolidated facts and records.
   */
  function openGroupSubjectModal(group) {
    if (!group) return;
    selectPotentialMatchAsTarget(group);
    const cf = group.consolidatedFacts || {};
    const mergedLinks = [];
    const seenUrls = new Set();
    for (const entry of group.entries || []) {
      for (const link of entry.attachedLinks || []) {
        if (link?.url && !seenUrls.has(link.url)) {
          seenUrls.add(link.url);
          mergedLinks.push(link);
        }
      }
    }
    openSubjectModal({
      id: group.id,
      title: group.name,
      summary: group.simplifiedSummary || group.subtitle || '',
      avatar: group.avatar || null,
      isBestMatch: group.isBestMatch,
      friendlyCategory: group.entityTypeLabel || '🔎 Grouped Dossier',
      source: (group.sources || []).join(' · ') || 'Multi-Source Public Records',
      confidence: 'public-record',
      url: cf.websites?.[0] || group.entries?.[0]?.url || null,
      corroborations: group.corroborations || [],
      extractedFields: {
        name: cf.fullName || group.name,
        entityKind: group.entityKind || 'person',
        age: cf.age || undefined,
        birthYear: cf.birthYear || undefined,
        estimatedAge: cf.estimatedAge || undefined,
        location: cf.addresses?.[0] || undefined,
        address: cf.addresses?.[0] || undefined,
        phone: cf.phones?.[0] || undefined,
        email: cf.emails?.[0] || undefined,
        username: cf.handles?.[0] || undefined,
        website: cf.websites?.[0] || undefined,
        organization: cf.organizations?.[0] || undefined,
      },
      provenance: {
        source: (group.sources || []).join(' · ') || 'Multi-Source Sweep',
        confidence: `${group.entryCount || group.entries?.length || 1} corroborated records`,
        pulledVia: 'Simple Mode Grouped Dossier',
        url: cf.websites?.[0] || group.entries?.[0]?.url || null,
      },
      attachedLinks: mergedLinks,
      relatedFindings: group.entries || [],
    });
  }

  const isPinned = (id) => pinnedItems().some(p => p.id === id);

  function togglePin(item) {
    const curr = pinnedItems();
    if (curr.some(p => p.id === item.id)) {
      setPinnedItems(curr.filter(p => p.id !== item.id));
    } else {
      setPinnedItems([
        ...curr,
        {
          ...item,
          pinnedTarget: result()?.target || simpleResult()?.summaryCard?.title || targetInput().trim() || 'manual',
          pinnedAt: new Date().toISOString(),
        },
      ]);
    }
  }

  // Schedule automatic debounced refresh when user adds more clues (e.g. State "NC" or Age) after initial search
  function notifySimpleFieldChanged() {
    if (!hasSearchedSimple()) return;
    if (autoRefreshTimer) clearTimeout(autoRefreshTimer);
    setAutoRefreshing(true);
    autoRefreshTimer = setTimeout(() => {
      runSimpleSearch({ silent: true });
    }, 650);
  }

  async function handleSimpleMediaAttach(file) {
    if (!file) return;
    setSimpleMediaBusy(true);
    try {
      const clues = await extractMediaFileClues(file);
      setSimpleMedia(clues);
      setFileReport(clues);
      if (!simpleEmail().trim() && clues.emails.length > 0) {
        setSimpleEmail(clues.emails[0]);
      }
      if (!simpleWebsite().trim() && clues.urls.length > 0) {
        setSimpleWebsite(clues.urls[0]);
      }
      if (hasSearchedSimple()) {
        notifySimpleFieldChanged();
      }
    } finally {
      setSimpleMediaBusy(false);
    }
  }

  function clearSimpleForm() {
    if (autoRefreshTimer) clearTimeout(autoRefreshTimer);
    setSimpleName('');
    setSimpleAge('');
    setSimpleLocation('');
    setSimpleUsername('');
    setSimpleEmail('');
    setSimplePhone('');
    setSimpleWebsite('');
    setSimpleExtra('');
    setSimpleMedia(null);
    setSimpleResult(null);
    setHasSearchedSimple(false);
    setAutoRefreshing(false);
    setPortedBanner(null);
    setPortedFieldKeys([]);
    setTargetedGroupId(null);
    setExpandedGroupIds({});
    setError(null);
  }

  async function runSimpleSearch(opts = {}) {
    if (autoRefreshTimer) {
      clearTimeout(autoRefreshTimer);
      autoRefreshTimer = null;
    }

    const payload = {
      name: simpleName().trim(),
      age: simpleAge().trim(),
      location: simpleLocation().trim(),
      username: simpleUsername().trim(),
      email: simpleEmail().trim(),
      phone: simplePhone().trim(),
      website: simpleWebsite().trim(),
      extraClues: simpleExtra().trim(),
      mediaClues: simpleMedia(),
    };

    if (!payload.name && !payload.age && !payload.location && !payload.username && !payload.email && !payload.phone && !payload.website && !payload.extraClues && !payload.mediaClues) {
      if (!opts.silent) {
        setError('Fill in at least one box above (like a Name, State, Age, Username, Email, or Phone Number) to find matches.');
      }
      setAutoRefreshing(false);
      return;
    }

    if (!opts.silent) {
      setLoading(true);
      setSimpleFilter('all');
    } else {
      setAutoRefreshing(true);
    }
    setError(null);

    try {
      const res = await fetch('/api/v2/osint/simple-search', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (!opts.silent) setError(body.error || `Search failed (HTTP ${res.status}).`);
        return;
      }
      setHasSearchedSimple(true);
      setSimpleResult(body);
      if (Array.isArray(body.potentialMatches) && body.potentialMatches.length > 0 && !opts.silent) {
        setTargetedGroupId(body.potentialMatches[0].id);
      }
      if (body.proResult) {
        setResult(body.proResult);
      }
    } catch (err) {
      if (!opts.silent) setError(err.message || 'Network error while searching.');
    } finally {
      setLoading(false);
      setAutoRefreshing(false);
    }
  }

  function handleSimpleKeyDown(e) {
    if (e.key === 'Enter' && !e.isComposing) {
      e.preventDefault();
      runSimpleSearch();
    }
  }

  function scanCrossReferencedHandle(handle) {
    if (!handle) return;
    const clean = String(handle).replace(/^@+/, '').trim();
    if (!clean) return;
    setSimpleUsername(clean);
    setPortedFieldKeys(['username']);
    setPortedBanner(`🔗 Cross-referenced handle @${clean} ported into Username box — refreshing dossier!`);
    runSimpleSearch({ silent: true });
    setTimeout(() => setPortedFieldKeys([]), 3000);
  }

  async function runDeepPullForSubject(overrideSubject = null) {
    const subj = overrideSubject || selectedSubject();
    const sf = subj?.extractedFields || {};
    const tg = activeTargetedGroup();
    const tcf = tg?.consolidatedFacts || {};
    const tsp = tg?.targetSpec || {};
    const sp = simpleResult()?.subjectProfile || {};

    const payload = {
      name: sf.name || tsp.name || tcf.fullName || simpleName().trim() || sp.fullName || '',
      age: sf.age || tsp.age || tcf.age || simpleAge().trim() || sp.age || '',
      location: sf.location || tsp.location || (tcf.addresses && tcf.addresses[0]) || simpleLocation().trim() || (sp.addresses && sp.addresses[0]) || '',
      username: sf.username || tsp.username || (tcf.handles && tcf.handles[0]) || simpleUsername().trim() || '',
      email: sf.email || tsp.email || (tcf.emails && tcf.emails[0]) || simpleEmail().trim() || (sp.emails && sp.emails[0]) || '',
      phone: sf.phone || tsp.phone || (tcf.phones && tcf.phones[0]) || simplePhone().trim() || (sp.phones && sp.phones[0]) || '',
      website: sf.website || tsp.website || (tcf.websites && tcf.websites[0]) || simpleWebsite().trim() || '',
      extraClues: simpleExtra().trim() || tsp.extraClues || '',
      mediaClues: simpleMedia(),
    };

    setDeepPullBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/v2/osint/deep-subject-pull', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body.error || `Deep pull failed (HTTP ${res.status}).`);
        return;
      }
      setSimpleResult(body);
      if (uiMode() !== 'simple') {
        setShowFrameworkPanel(true);
      }
      if (body.proResult) setResult(body.proResult);
      if (selectedSubject() && body.bestMatch) {
        setSelectedSubject({
          ...selectedSubject(),
          subjectProfile: body.subjectProfile,
          relatedFindings: (body.matches || []).slice(0, 14),
        });
      }
      setPortedBanner(
        `⚡ Deep Public Dossier complete for ${payload.name || 'target'}: checked ${body.sourcesChecked || 25}+ public registries and webpages!`,
      );
    } catch (err) {
      setError(err.message || 'Network error during deep subject pull.');
    } finally {
      setDeepPullBusy(false);
    }
  }

  // "Port This Data to Widget" — populates any field in the widget that the clicked record has data on!
  function portRecordToWidget(item, closeModal = true) {
    if (!item) return;
    const f = item.extractedFields || {};
    const text = `${item.title || ''} ${item.summary || ''} ${(item.meta || []).join(' ')}`;

    const ported = [];
    const keys = [];

    const nameVal = f.name || (item.title && item.title.includes('·') ? item.title.split('·')[1].replace(/\([^)]*\)/g, '').trim() : '');
    if (nameVal && !nameVal.startsWith('@') && !nameVal.startsWith('+')) {
      setSimpleName(nameVal);
      ported.push(`Name (${nameVal})`);
      keys.push('name');
    }

    if (f.age) {
      setSimpleAge(String(f.age));
      ported.push(`Age (${f.age})`);
      keys.push('age');
    }

    if (f.location) {
      setSimpleLocation(f.location);
      ported.push(`Location (${f.location})`);
      keys.push('location');
    }

    const handleVal = f.username || (text.match(/(?:^|[\s(])@([a-zA-Z0-9_-]{2,39})\b/) || [])[1];
    if (handleVal) {
      setSimpleUsername(handleVal.replace(/^@+/, ''));
      ported.push(`Username (@${handleVal.replace(/^@+/, '')})`);
      keys.push('username');
    }

    const emailVal = f.email || (text.match(/\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,18})\b/) || [])[1];
    if (emailVal) {
      setSimpleEmail(emailVal.toLowerCase());
      ported.push(`Email (${emailVal.toLowerCase()})`);
      keys.push('email');
    }

    const phoneVal = f.phone || (text.match(/(\+1\d{10}|\+\d{1,3}[\s.-]?\(?\d{2,4}\)?[\s.-]?\d{3,4}[\s.-]?\d{3,4})/) || [])[1];
    if (phoneVal) {
      setSimplePhone(phoneVal);
      ported.push(`Phone (${phoneVal})`);
      keys.push('phone');
    }

    const webVal = f.website || (item.url && /^https?:\/\//i.test(item.url) ? item.url : '');
    if (webVal) {
      setSimpleWebsite(webVal);
      ported.push('Website / Profile Link');
      keys.push('website');
    }

    if (f.organization) {
      setSimpleExtra(f.organization);
      ported.push(`Organization (${f.organization})`);
      keys.push('extra');
    }

    const proTarget = handleVal || emailVal || nameVal || phoneVal || webVal;
    if (proTarget) {
      setTargetInput(proTarget);
    }

    setPortedFieldKeys(keys);
    setPortedBanner(
      ported.length > 0
        ? `📥 Ported ${ported.length} field${ported.length === 1 ? '' : 's'} into the widget: ${ported.join(' · ')}`
        : `📥 Ported "${item.title}" into the widget search fields.`,
    );

    if (closeModal) {
      setSelectedSubject(null);
    }

    runSimpleSearch({ silent: true });

    setTimeout(() => {
      setPortedFieldKeys([]);
    }, 3200);
  }

  // Open Subject Dossier Modal & initialize subject-specific Fissal chat
  function openSubjectModal(item) {
    if (subjectChatAbort) subjectChatAbort.abort();
    setSelectedSubject(item);
    setSubjectToolsUsed([]);
    setSubjectChatInput('');
    setSubjectChatMessages([
      {
        role: 'fissal',
        text: `This one has opened the subject dossier for "${item.title}" (pulled from ${item.source || 'public records'}). Ask Fissal anything below—these paws can dig deeper into their handles, commit history, public records, or verify how they match your clues!`,
      },
    ]);
  }

  async function askSubjectFissal(customQuestion) {
    const subj = selectedSubject();
    if (!subj) return;
    const question = String(customQuestion ?? subjectChatInput()).trim();
    if (!question) return;

    if (!customQuestion) setSubjectChatInput('');
    setSubjectChatMessages(prev => [
      ...prev,
      { role: 'user', text: question },
      { role: 'fissal', text: '', streaming: true },
    ]);

    if (subjectChatAbort) subjectChatAbort.abort();
    subjectChatAbort = new AbortController();
    setSubjectChatStreaming(true);
    setSubjectToolsUsed([]);

    const f = subj.extractedFields || {};
    const contextLines = [
      `Selected Subject Entry: ${subj.title}`,
      `Source / Provenance: ${subj.source} (Pulled via: ${subj.provenance?.pulledVia || subj.matchedVia || 'OSINT Sweep'}, URL: ${subj.url || 'N/A'})`,
      `Summary: ${subj.summary}`,
      `Extracted Subject Fields: ${JSON.stringify(f)}`,
      `Corroborated Clues: ${(subj.corroborations || []).join(' | ')}`,
      `Attached Links: ${(subj.attachedLinks || []).map(l => `${l.label}: ${l.url}`).join(', ')}`,
      `User's Entered Search Clues: Name="${simpleName()}", Age="${simpleAge()}", Location="${simpleLocation()}", Username="${simpleUsername()}", Email="${simpleEmail()}", Phone="${simplePhone()}", Extra="${simpleExtra()}"`,
      `Related Findings for Subject: ${(subj.relatedFindings || []).map(r => `[${r.source}] ${r.title}: ${r.summary}`).join(' ; ')}`,
    ].join('\n');

    try {
      const response = await fetch('/api/v2/osint/copilot', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        signal: subjectChatAbort.signal,
        body: JSON.stringify({
          prompt: question,
          target: f.username || f.email || f.name || subj.title,
          mode: f.username ? 'username' : f.email ? 'email' : 'auto',
          findingsContext: contextLines,
        }),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || `Fissal returned HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('Streaming unavailable.');
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const payloadStr = trimmed.slice(5).trim();
          if (!payloadStr || payloadStr === '[DONE]') continue;
          try {
            const evt = JSON.parse(payloadStr);
            if (evt.type === 'tool_start') {
              setSubjectToolsUsed(prev => [...prev, { name: evt.name, args: evt.args, status: 'running', count: 0 }]);
            } else if (evt.type === 'tool_result') {
              setSubjectToolsUsed(prev =>
                prev.map(t => (t.name === evt.name && t.status === 'running' ? { ...t, status: 'done', count: evt.count || 0 } : t)),
              );
              if (Array.isArray(evt.findings) && evt.findings.length > 0) {
                setSelectedSubject(prevSubj => {
                  if (!prevSubj) return prevSubj;
                  const existingRel = prevSubj.relatedFindings || [];
                  const existingIds = new Set(existingRel.map(r => r.id));
                  const added = evt.findings
                    .filter(nf => !existingIds.has(nf.id))
                    .map(nf => ({
                      id: nf.id,
                      title: nf.title,
                      summary: nf.summary,
                      source: `${nf.source} · 🧠 Fissal`,
                      url: nf.url,
                      type: nf.type,
                    }));
                  return {
                    ...prevSubj,
                    relatedFindings: [...added, ...existingRel].slice(0, 14),
                  };
                });
              }
            } else if (evt.type === 'chunk' && evt.text) {
              accumulated += evt.text;
              const currentText = accumulated;
              setSubjectChatMessages(prev => {
                const copy = [...prev];
                if (copy.length > 0 && copy[copy.length - 1].role === 'fissal') {
                  copy[copy.length - 1] = { role: 'fissal', text: currentText, streaming: true };
                }
                return copy;
              });
            } else if (evt.type === 'done' && evt.final_text && !accumulated) {
              accumulated = evt.final_text;
              setSubjectChatMessages(prev => {
                const copy = [...prev];
                if (copy.length > 0 && copy[copy.length - 1].role === 'fissal') {
                  copy[copy.length - 1] = { role: 'fissal', text: accumulated, streaming: false };
                }
                return copy;
              });
            } else if (evt.type === 'error') {
              throw new Error(evt.message || 'Fissal encountered an error.');
            }
          } catch { /* ignore partial frame */ }
        }
      }

      setSubjectChatMessages(prev => {
        const copy = [...prev];
        if (copy.length > 0 && copy[copy.length - 1].role === 'fissal') {
          copy[copy.length - 1] = {
            role: 'fissal',
            text: copy[copy.length - 1].text || 'Investigation complete.',
            streaming: false,
          };
        }
        return copy;
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        setSubjectChatMessages(prev => {
          const copy = [...prev];
          if (copy.length > 0 && copy[copy.length - 1].role === 'fissal') {
            copy[copy.length - 1] = {
              role: 'fissal',
              text: `⚠️ ${err.message || 'Could not reach Fissal Co-Pilot.'}`,
              streaming: false,
            };
          }
          return copy;
        });
      }
    } finally {
      setSubjectChatStreaming(false);
    }
  }

  async function runSearch(overrideTarget, overrideMode) {
    const target = String(overrideTarget ?? targetInput()).trim();
    const mode = overrideMode || selectedMode();
    if (!target) {
      setError('Enter a target to investigate.');
      return;
    }
    if (overrideTarget !== undefined) setTargetInput(target);
    if (overrideMode && overrideMode !== 'auto') setSelectedMode(overrideMode);

    setLoading(true);
    setError(null);
    setActiveFilter('all');
    setActiveView('feed');

    try {
      const res = await fetch('/api/v2/osint/search', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target, mode }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body.error || `Lookup failed (HTTP ${res.status}).`);
        return;
      }
      setResult(body);
      setTrail(prev => {
        const filtered = prev.filter(t => !(t.target === body.target && t.mode === body.mode));
        return [...filtered.slice(-9), { target: body.target, mode: body.mode, count: (body.findings || []).length }];
      });
    } catch (err) {
      setError(err.message || 'Network error while executing OSINT lookup.');
    } finally {
      setLoading(false);
    }
  }

  function handleSearchKeyDown(e) {
    if (e.key === 'Enter' && !e.isComposing) {
      e.preventDefault();
      runSearch();
    }
  }

  async function copyFinding(item) {
    try {
      const text = `${item.title}\n${item.summary}\nSource: ${item.source} (${item.url || ''})`;
      await navigator.clipboard.writeText(text);
      setCopiedId(item.id);
      setTimeout(() => {
        if (copiedId() === item.id) setCopiedId(null);
      }, 1600);
    } catch { /* ignore */ }
  }

  function exportCaseBoard(format = 'md') {
    const items = pinnedItems();
    const notes = caseNotes().trim();
    const ts = new Date().toISOString();
    let content = '';
    let mime = 'text/markdown';
    let ext = 'md';

    if (format === 'json') {
      content = JSON.stringify({
        exportedAt: ts,
        trail: trail(),
        notes,
        evidenceCount: items.length,
        evidence: items,
      }, null, 2);
      mime = 'application/json';
      ext = 'json';
    } else {
      const lines = [
        `# Sovereign OSINT Investigation Dossier`,
        `- **Generated**: ${ts}`,
        `- **Investigation Trail**: ${trail().map(t => `${t.target} (${t.mode})`).join(' → ') || 'Direct'}`,
        `- **Pinned Evidence Count**: ${items.length}`,
        '',
      ];
      if (notes) {
        lines.push(`## Analyst Notes`, notes, '');
      }
      lines.push(`## Pinned Evidence`);
      for (const it of items) {
        lines.push(
          `### [${(it.type || 'intel').toUpperCase()}] ${it.title}`,
          `- **Target Context**: \`${it.pinnedTarget}\``,
          `- **Source**: ${it.source} (${it.confidence || 'direct'})`,
          it.url ? `- **Verification URL**: ${it.url}` : null,
          `- **Summary**: ${it.summary}`,
          '',
        );
      }
      content = lines.filter(l => l !== null).join('\n');
    }

    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fissal-osint-dossier-${ts.slice(0, 10)}.${ext}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  async function inspectLocalFile(file) {
    if (!file) return;
    setFileAnalyzing(true);
    try {
      const report = await extractMediaFileClues(file);
      setFileReport(report);
    } finally {
      setFileAnalyzing(false);
    }
  }

  async function askFissalCopilot(customPrompt) {
    const resData = result();
    const FindingsSummary = resData
      ? [
          `Target: ${resData.target} (mode: ${resData.mode})`,
          `Summary: ${resData.profileCard?.title} — ${resData.profileCard?.summary}`,
          `Findings (${resData.findings?.length || 0}):`,
          ...(resData.findings || []).slice(0, 22).map(f => `- [${f.type}] ${f.title} (${f.source}): ${f.summary}`),
        ].join('\n')
      : '';

    const userQuestion = (customPrompt || fissalPrompt()).trim() ||
      (resData
        ? `Synthesize an OSINT intelligence brief for ${resData.target}. Highlight key correlations, security posture, and call your OSINT tools on the highest-value pivot leads.`
        : 'What target should we investigate?');

    if (fissalAbort) fissalAbort.abort();
    fissalAbort = new AbortController();

    setUiMode('pro');
    setActiveView('fissal');
    setFissalStreaming(true);
    setFissalError(null);
    setFissalResponse('');
    setFissalToolsUsed([]);

    try {
      const response = await fetch('/api/v2/osint/copilot', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        signal: fissalAbort.signal,
        body: JSON.stringify({
          prompt: userQuestion,
          target: resData?.target || targetInput().trim() || '',
          mode: resData?.mode || selectedMode(),
          findingsContext: FindingsSummary,
        }),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || `Fissal Co-Pilot returned HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('Streaming unavailable.');
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const payloadStr = trimmed.slice(5).trim();
          if (!payloadStr || payloadStr === '[DONE]') continue;
          try {
            const evt = JSON.parse(payloadStr);
            if (evt.type === 'tool_start') {
              setFissalToolsUsed(prev => [...prev, { name: evt.name, args: evt.args, status: 'running', count: 0 }]);
            } else if (evt.type === 'tool_result') {
              setFissalToolsUsed(prev =>
                prev.map(t => (t.name === evt.name && t.status === 'running' ? { ...t, status: 'done', count: evt.count || 0 } : t)),
              );
              if (Array.isArray(evt.findings) && evt.findings.length > 0) {
                setResult(prev => {
                  if (!prev) {
                    return {
                      ok: true,
                      mode: evt.args?.mode || 'auto',
                      target: evt.args?.target || evt.args?.username || evt.args?.query || 'Co-Pilot Sweep',
                      searchedAt: new Date().toISOString(),
                      elapsedMs: 0,
                      sourcesChecked: evt.findings.length,
                      profileCard: evt.profileCard || {
                        kind: 'Fissal Co-Pilot Autonomous Sweep',
                        title: evt.args?.target || evt.args?.username || evt.args?.query || 'Co-Pilot Target',
                        subtitle: `Collected via ${evt.name}`,
                        summary: `Fissal autonomously pulled ${evt.findings.length} public records.`,
                        badges: [`${evt.findings.length} records`],
                      },
                      findings: evt.findings,
                      pivots: evt.pivots || [],
                      references: [],
                    };
                  }
                  const existingIds = new Set((prev.findings || []).map(f => f.id));
                  const merged = [...(prev.findings || [])];
                  for (const nf of evt.findings) {
                    if (!existingIds.has(nf.id)) {
                      existingIds.add(nf.id);
                      merged.push({ ...nf, source: `${nf.source} · 🧠 Fissal Tool` });
                    }
                  }
                  return { ...prev, findings: merged };
                });
              }
            } else if (evt.type === 'chunk' && evt.text) {
              setFissalResponse(prev => prev + evt.text);
            } else if (evt.type === 'done' && evt.final_text && !fissalResponse()) {
              setFissalResponse(evt.final_text);
            } else if (evt.type === 'error') {
              setFissalError(evt.message || 'Fissal Co-Pilot encountered an error.');
            }
          } catch { /* ignore partial frame */ }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setFissalError(err.message || 'Could not reach Fissal Co-Pilot.');
      }
    } finally {
      setFissalStreaming(false);
    }
  }

  const isStaff = () => !!(wm.state.identity?.isAdmin || wm.state.identity?.isOwner);

  return (
    <Show when={isStaff()} fallback={
      <div class="v2-empty">
        🔒 Staff authorization is required to access Sovereign OSINT tools.
      </div>
    }>
    <div class="osint-Research">
      {/* 1. Sovereign Header + Simple / Pro Mode Switcher */}
      <header class="osint-Research__header">
        <div class="osint-Research__titleGroup">
          <div class="osint-Research__kicker">
            {uiMode() === 'simple'
              ? 'EASY PEOPLE, CONTACT & PUBLIC RECORD FINDER'
              : 'SOVEREIGN MULTI-SOURCE INTELLIGENCE · SWISS ARMY KNIFE'}
          </div>
          <h3 class="osint-Research__title">
            {uiMode() === 'simple' ? 'Quick Match Finder' : 'Find By Target'}
            <span class="osint-Research__sovereignBadge" title="Runs directly on echodedicated without third-party tracking">
              ⚡ Private & Local
            </span>
          </h3>
        </div>

        <div class="osint-Research__headerRight">
          {/* Simple Mode vs Pro Mode Toggle */}
          <div class="osint-Research__modeSwitch" role="group" aria-label="Interface Complexity Mode">
            <button
              type="button"
              class="osint-Research__modeSwitchBtn"
              classList={{ 'is-active': uiMode() === 'simple' }}
              onClick={() => setUiMode('simple')}
              title="Clean, guided form where you can enter any combination of details and find matches with one click"
            >
              ✨ Simple Mode
            </button>
            <button
              type="button"
              class="osint-Research__modeSwitchBtn"
              classList={{ 'is-active': uiMode() === 'pro' }}
              onClick={() => setUiMode('pro')}
              title="Full 11-mode OSINT workstation with multi-hop pivoting, Case Board, File Lab, and Fissal Co-Pilot"
            >
              🛠️ Pro Mode
            </button>
          </div>

          <Show when={uiMode() === 'pro'}>
            <div class="osint-Research__viewTabs" role="tablist" aria-label="OSINT Workspace Views">
              <button
                type="button"
                role="tab"
                aria-selected={activeView() === 'feed'}
                class="osint-Research__viewTab"
                classList={{ 'is-active': activeView() === 'feed' }}
                onClick={() => setActiveView('feed')}
              >
                📡 Feed {findings().length > 0 ? `(${findings().length})` : ''}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeView() === 'caseboard'}
                class="osint-Research__viewTab"
                classList={{ 'is-active': activeView() === 'caseboard' }}
                onClick={() => setActiveView('caseboard')}
              >
                📌 Case Board {pinnedItems().length > 0 ? `(${pinnedItems().length})` : ''}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeView() === 'filelab'}
                class="osint-Research__viewTab"
                classList={{ 'is-active': activeView() === 'filelab' }}
                onClick={() => setActiveView('filelab')}
              >
                🧬 File Lab
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeView() === 'fissal'}
                class="osint-Research__viewTab osint-Research__viewTab--fissal"
                classList={{ 'is-active': activeView() === 'fissal' }}
                onClick={() => setActiveView('fissal')}
              >
                🧠 Fissal Co-Pilot
              </button>
            </div>
          </Show>
        </div>
      </header>

      {/* Error Banner (shared) */}
      <Show when={error()}>
        <div class="osint-Research__error" role="alert">
          <span>⚠️ {error()}</span>
          <button type="button" class="osint-Research__errorClose" onClick={() => setError(null)}>✕</button>
        </div>
      </Show>

      {/* Ported Data Notification Banner */}
      <Show when={portedBanner()}>
        <div class="osint-Research__portedBanner" role="status">
          <span>{portedBanner()}</span>
          <button type="button" class="osint-Research__errorClose" onClick={() => setPortedBanner(null)}>✕</button>
        </div>
      </Show>

      {/* ═══════════════════════════════════════════════════════════════════
          SIMPLE MODE (CLEAN, BEGINNER-FRIENDLY INDIVIDUAL FIND CARDS)
         ═══════════════════════════════════════════════════════════════════ */}
      <Show when={uiMode() === 'simple'}>
        <section class="osint-Research__simpleCard">
          <div class="osint-Research__simpleIntro">
            <div>
              <h4 class="osint-Research__simpleHeading">
                Find a Person, Business, or Contact — <span class="osint-Research__bestGold">Grouped & Easy to Read</span>
              </h4>
              <p class="osint-Research__simpleSub">
                Type any detail you know below (a name, business, state like <strong>NC</strong>, age like <strong>30-40</strong>, phone, or username). Each match is grouped into one clean card with their phone number, address, and details together—click any card to focus on it.
              </p>
            </div>
            <div class="osint-Research__simpleQuickTry">
              <span class="osint-Research__examplesLabel">Try an example:</span>
              <button
                type="button"
                class="osint-Research__exampleChip"
                onClick={() => {
                  setSimpleName('Brandon Shane Merrill');
                  setSimpleAge('');
                  setSimpleLocation('NC');
                  setSimpleUsername('');
                  setSimplePhone('');
                  setSimpleEmail('');
                  setSimpleWebsite('');
                  setSimpleExtra('');
                  runSimpleSearch();
                }}
              >
                👤 Brandon Shane Merrill (NC)
              </button>
              <button
                type="button"
                class="osint-Research__exampleChip"
                onClick={() => {
                  setSimpleName('Cloudflare Inc');
                  setSimpleAge('');
                  setSimpleLocation('San Francisco, CA');
                  setSimpleUsername('');
                  setSimplePhone('');
                  setSimpleEmail('');
                  setSimpleWebsite('');
                  setSimpleExtra('');
                  runSimpleSearch();
                }}
              >
                🏢 Cloudflare Inc (Business)
              </button>
              <button
                type="button"
                class="osint-Research__exampleChip"
                onClick={() => {
                  setSimpleName('Linus Torvalds');
                  setSimpleAge('50-60');
                  setSimpleLocation('OR');
                  setSimpleUsername('');
                  setSimplePhone('');
                  setSimpleEmail('');
                  setSimpleWebsite('');
                  setSimpleExtra('Linux');
                  runSimpleSearch();
                }}
              >
                👤 Linus Torvalds (OR)
              </button>
            </div>
          </div>

          <div class="osint-Research__simpleGrid osint-Research__simpleGrid--8">
            <label class="osint-Research__simpleField">
              <span class="osint-Research__simpleLabel">👤 Name or Business</span>
              <input
                type="text"
                class="osint-Research__simpleInput"
                classList={{ 'is-ported': portedFieldKeys().includes('name') }}
                placeholder="e.g. Brandon Shane Merrill"
                value={simpleName()}
                onInput={e => {
                  setSimpleName(e.currentTarget.value);
                  notifySimpleFieldChanged();
                }}
                onKeyDown={handleSimpleKeyDown}
              />
            </label>

            <label class="osint-Research__simpleField">
              <span class="osint-Research__simpleLabel">📍 State, City, or Address</span>
              <input
                type="text"
                class="osint-Research__simpleInput"
                classList={{ 'is-ported': portedFieldKeys().includes('location') }}
                placeholder="e.g. NC, Raleigh NC, or Austin TX"
                value={simpleLocation()}
                onInput={e => {
                  setSimpleLocation(e.currentTarget.value);
                  notifySimpleFieldChanged();
                }}
                onKeyDown={handleSimpleKeyDown}
              />
            </label>

            <label class="osint-Research__simpleField">
              <span class="osint-Research__simpleLabel">🎂 Age, Age Range, or Birth Year</span>
              <input
                type="text"
                class="osint-Research__simpleInput"
                classList={{ 'is-ported': portedFieldKeys().includes('age') }}
                placeholder="e.g. 33, 30-40, or 1993"
                value={simpleAge()}
                onInput={e => {
                  setSimpleAge(e.currentTarget.value);
                  notifySimpleFieldChanged();
                }}
                onKeyDown={handleSimpleKeyDown}
              />
            </label>

            <label class="osint-Research__simpleField">
              <span class="osint-Research__simpleLabel">🏷️ Username or Handle</span>
              <input
                type="text"
                class="osint-Research__simpleInput"
                classList={{ 'is-ported': portedFieldKeys().includes('username') }}
                placeholder="e.g. @echotiger or bsmx93"
                value={simpleUsername()}
                onInput={e => {
                  setSimpleUsername(e.currentTarget.value);
                  notifySimpleFieldChanged();
                }}
                onKeyDown={handleSimpleKeyDown}
              />
            </label>

            <label class="osint-Research__simpleField">
              <span class="osint-Research__simpleLabel">✉️ Email Address</span>
              <input
                type="email"
                class="osint-Research__simpleInput"
                classList={{ 'is-ported': portedFieldKeys().includes('email') }}
                placeholder="e.g. user@example.com"
                value={simpleEmail()}
                onInput={e => {
                  setSimpleEmail(e.currentTarget.value);
                  notifySimpleFieldChanged();
                }}
                onKeyDown={handleSimpleKeyDown}
              />
            </label>

            <label class="osint-Research__simpleField">
              <span class="osint-Research__simpleLabel">📞 Phone Number</span>
              <input
                type="tel"
                class="osint-Research__simpleInput"
                classList={{ 'is-ported': portedFieldKeys().includes('phone') }}
                placeholder="e.g. 919-555-0199"
                value={simplePhone()}
                onInput={e => {
                  setSimplePhone(e.currentTarget.value);
                  notifySimpleFieldChanged();
                }}
                onKeyDown={handleSimpleKeyDown}
              />
            </label>

            <label class="osint-Research__simpleField">
              <span class="osint-Research__simpleLabel">🌐 Website or Social Link</span>
              <input
                type="text"
                class="osint-Research__simpleInput"
                classList={{ 'is-ported': portedFieldKeys().includes('website') }}
                placeholder="e.g. example.com or profile URL"
                value={simpleWebsite()}
                onInput={e => {
                  setSimpleWebsite(e.currentTarget.value);
                  notifySimpleFieldChanged();
                }}
                onKeyDown={handleSimpleKeyDown}
              />
            </label>

            <label class="osint-Research__simpleField">
              <span class="osint-Research__simpleLabel">🧩 Loose Clues (School, Job, Bio)</span>
              <input
                type="text"
                class="osint-Research__simpleInput"
                classList={{ 'is-ported': portedFieldKeys().includes('extra') }}
                placeholder="e.g. echotiger, Engineer, Linux"
                value={simpleExtra()}
                onInput={e => {
                  setSimpleExtra(e.currentTarget.value);
                  notifySimpleFieldChanged();
                }}
                onKeyDown={handleSimpleKeyDown}
              />
            </label>
          </div>

          {/* Optional Media / Photo Dropbar + Primary Action Button */}
          <div class="osint-Research__simpleActionsRow">
            <div class="osint-Research__simpleMediaWrap">
              <label class="osint-Research__simpleMediaBtn">
                {simpleMediaBusy() ? 'Checking file…' : '🖼️ Attach Photo or File (Optional)'}
                <input
                  type="file"
                  style={{ display: 'none' }}
                  onChange={e => handleSimpleMediaAttach(e.currentTarget.files?.[0])}
                />
              </label>
              <Show when={simpleMedia()}>
                {m => (
                  <span class="osint-Research__simpleMediaPill">
                    📎 {m().fileName}
                    {m().dimensions ? ` (${m().dimensions})` : ''}
                    <button
                      type="button"
                      class="osint-Research__simpleMediaRemove"
                      onClick={() => {
                        setSimpleMedia(null);
                        notifySimpleFieldChanged();
                      }}
                      title="Remove attached file"
                    >
                      ✕
                    </button>
                  </span>
                )}
              </Show>
              <Show when={autoRefreshing()}>
                <span class="osint-Research__autoRefreshPill">
                  🔄 Refreshing closest matches for your updated clues…
                </span>
              </Show>
            </div>

            <div class="osint-Research__simpleActionBtns">
              <button
                type="button"
                class="osint-Research__simpleClearBtn"
                onClick={clearSimpleForm}
              >
                Clear
              </button>
              <button
                type="button"
                class="osint-Research__simpleSubmitBtn"
                disabled={loading()}
                onClick={() => runSimpleSearch()}
              >
                {loading() ? '🔍 Searching Public Sources…' : '🔍 Find Potential Matches'}
              </button>
            </div>
          </div>
        </section>

        {/* Simple Mode Results — Clean, Beginner-Friendly Individual Find Cards */}
        <Show when={simpleResult()}>
          {sr => (
            <div class="osint-Research__simpleResults">
              {/* Compact Active Target Status Bar */}
              <div class="osint-Research__simpleStatusBar">
                <div class="osint-Research__simpleStatusLeft">
                  <span class="osint-Research__simpleStatusCount">
                    ✅ Found <strong>{livePotentialMatchGroups().length}</strong> Potential Match{livePotentialMatchGroups().length === 1 ? '' : 'es'}
                  </span>
                  <Show when={activeTargetedGroup()}>
                    {tg => (
                      <span class="osint-Research__activeTargetPill">
                        🎯 Active Target: <strong>{tg().name}</strong>
                      </span>
                    )}
                  </Show>
                  <For each={sr().cluesUsed || []}>
                    {clue => <span class="osint-Research__badge">{clue}</span>}
                  </For>
                </div>

                <div class="osint-Research__simpleStatusActions">
                  <button
                    type="button"
                    class="osint-Research__deepPullBtn"
                    disabled={deepPullBusy()}
                    onClick={() => runDeepPullForSubject()}
                    title="Search additional public directories and social networks for the active target"
                  >
                    {deepPullBusy() ? '⚡ Searching More Sources…' : '⚡ Find More Info on Target'}
                  </button>
                  <button
                    type="button"
                    class="osint-Research__askFissalBtn"
                    onClick={() =>
                      askFissalCopilot(
                        `Summarize everything found for ${activeTargetedGroup()?.name || sr().summaryCard?.title} in plain, simple language, including their phone number, location/address, and any related accounts or records.`,
                      )
                    }
                  >
                    🧠 Ask Fissal
                  </button>
                  <button
                    type="button"
                    class="osint-Research__frameworkToggleBtn"
                    onClick={() => setShowFrameworkPanel(!showFrameworkPanel())}
                  >
                    🌐 {showFrameworkPanel() ? 'Hide Extra Tools' : 'More Lookup Tools'}
                  </button>
                </div>
              </div>

              {/* Optional External Portals Drawer (Hidden by default to keep Simple Mode clean) */}
              <Show when={showFrameworkPanel() && (sr().osintFramework || []).length > 0}>
                <section class="osint-Research__frameworkPanel">
                  <div class="osint-Research__frameworkHeader">
                    <div>
                      <div class="osint-Research__kicker">
                        OPTIONAL EXTERNAL PORTALS · PRE-FILLED FOR CURRENT TARGET
                      </div>
                      <h4 class="osint-Research__frameworkTitle">
                        🌐 External Public Record Portals
                      </h4>
                    </div>
                    <button
                      type="button"
                      class="osint-Research__simpleClearBtn"
                      onClick={() => setShowFrameworkPanel(false)}
                    >
                      Close ✕
                    </button>
                  </div>

                  <div class="osint-Research__frameworkCategories">
                    <For each={sr().osintFramework}>
                      {cat => (
                        <div class="osint-Research__frameworkCatCard">
                          <div class="osint-Research__frameworkCatTitle">{cat.title}</div>
                          <div class="osint-Research__frameworkCatSub">{cat.subtitle}</div>
                          <div class="osint-Research__frameworkItems">
                            <For each={cat.items || []}>
                              {item => (
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  class="osint-Research__frameworkItem"
                                >
                                  <div class="osint-Research__frameworkItemTop">
                                    <strong>{item.name} ↗</strong>
                                    <Show when={item.badge}>
                                      <span class="osint-Research__frameworkItemBadge">{item.badge}</span>
                                    </Show>
                                  </div>
                                  <p>{item.desc}</p>
                                </a>
                              )}
                            </For>
                          </div>
                        </div>
                      )}
                    </For>
                  </div>
                </section>
              </Show>

              {/* Simple Category Filter Pills */}
              <div class="osint-Research__filterPills">
                <button
                  type="button"
                  class="osint-Research__filterPill"
                  classList={{ 'is-active': simpleFilter() === 'all' }}
                  onClick={() => setSimpleFilter('all')}
                >
                  All Matches ({livePotentialMatchGroups().length})
                </button>
                <button
                  type="button"
                  class="osint-Research__filterPill"
                  classList={{ 'is-active': simpleFilter() === 'strong' }}
                  onClick={() => setSimpleFilter('strong')}
                >
                  🏆 Best Matches
                </button>
                <button
                  type="button"
                  class="osint-Research__filterPill"
                  classList={{ 'is-active': simpleFilter() === 'location' }}
                  onClick={() => setSimpleFilter('location')}
                >
                  📍 Phone & Address
                </button>
                <button
                  type="button"
                  class="osint-Research__filterPill"
                  classList={{ 'is-active': simpleFilter() === 'profile' }}
                  onClick={() => setSimpleFilter('profile')}
                >
                  👤 Profiles & Socials
                </button>
                <button
                  type="button"
                  class="osint-Research__filterPill"
                  classList={{ 'is-active': simpleFilter() === 'records' }}
                  onClick={() => setSimpleFilter('records')}
                >
                  🏛️ Official Records
                </button>
              </div>

              {/* ── INDIVIDUAL FIND CARDS (One Clean Card Per Person, Business, or Profile) ── */}
              <div class="osint-Research__potentialGroupsList">
                <Show
                  when={filteredPotentialMatchGroups().length > 0}
                  fallback={
                    <div class="osint-Research__noMatches">
                      No matches found for this filter. Try switching back to "All Matches" or adding another detail above.
                    </div>
                  }
                >
                  <For each={filteredPotentialMatchGroups()}>
                    {group => {
                      const isTargeted = () => activeTargetedGroup()?.id === group.id;
                      const isExpanded = () => Boolean(expandedGroupIds()[group.id]);

                      return (
                        <article
                          class="osint-Research__potentialGroupCard"
                          classList={{
                            'is-best': group.isBestMatch,
                            'is-targeted': isTargeted(),
                            'is-strong': !group.isBestMatch && group.matchTier === 'strong',
                          }}
                          onClick={() => selectPotentialMatchAsTarget(group)}
                        >
                          {/* 1. Card Header: Name / Business Title + Entity Type + Match Badge + Target Action */}
                          <div class="osint-Research__potentialGroupTop">
                            <div class="osint-Research__potentialGroupIdentity">
                              <Show
                                when={group.avatar}
                                fallback={
                                  <div class="osint-Research__groupAvatarFallback">
                                    {group.entityKind === 'business' ? '🏢' : '👤'}
                                  </div>
                                }
                              >
                                <img src={group.avatar} alt="" class="osint-Research__groupAvatar" loading="lazy" />
                              </Show>
                              <div>
                                <div class="osint-Research__potentialGroupTitleRow">
                                  <h5 class="osint-Research__potentialGroupName">{group.name}</h5>
                                  <span class="osint-Research__entityKindChip">
                                    {group.entityTypeLabel || (group.entityKind === 'business' ? '🏢 Business / Organization' : '👤 Individual')}
                                  </span>
                                  <span
                                    class="osint-Research__conf"
                                    classList={{
                                      'osint-Research__conf--best': group.isBestMatch,
                                      'osint-Research__conf--local': !group.isBestMatch && group.matchTier === 'strong',
                                      'osint-Research__conf--api': !group.isBestMatch && group.matchTier === 'likely',
                                      'osint-Research__conf--record': !group.isBestMatch && group.matchTier === 'possible',
                                    }}
                                  >
                                    {group.matchBadge}
                                  </span>
                                  <Show when={isTargeted()}>
                                    <span class="osint-Research__targetedBadge">
                                      🎯 Active Target
                                    </span>
                                  </Show>
                                </div>
                                <div class="osint-Research__potentialGroupSub">{group.subtitle}</div>
                              </div>
                            </div>

                            <div class="osint-Research__potentialGroupActions" onClick={e => e.stopPropagation()}>
                              <button
                                type="button"
                                class="osint-Research__targetGroupBtn"
                                classList={{ 'is-active': isTargeted() }}
                                onClick={() => selectPotentialMatchAsTarget(group)}
                                title="Set the dashboard to target this specific match"
                              >
                                {isTargeted() ? '🎯 Targeted' : '🎯 Target This Match'}
                              </button>
                              <button
                                type="button"
                                class="osint-Research__inspectBtn"
                                onClick={() => fillSimpleFormFromGroup(group)}
                                title="Fill the search boxes above with this match's details"
                              >
                                📥 Load Into Search
                              </button>
                              <button
                                type="button"
                                class="osint-Research__inspectBtn"
                                onClick={() => openGroupSubjectModal(group)}
                                title="View full dossier and ask Fissal questions about this match"
                              >
                                💬 Ask Fissal
                              </button>
                            </div>
                          </div>

                          {/* 2. Grouped Information Grid (Phone Number, Location/Address, Age, Email, Website, Family, Registration) */}
                          <Show
                            when={(group.quickFacts || []).length > 0}
                            fallback={
                              <p class="osint-Research__groupPlainDigest">
                                {group.simplifiedSummary}
                              </p>
                            }
                          >
                            <div class="osint-Research__groupFactsGrid">
                              <For each={group.quickFacts}>
                                {fact => (
                                  <div class="osint-Research__groupFactPill">
                                    <span class="osint-Research__groupFactLabel">
                                      {fact.icon} {fact.label}
                                    </span>
                                    <strong class="osint-Research__groupFactValue">{fact.value}</strong>
                                  </div>
                                )}
                              </For>
                            </div>
                          </Show>

                          {/* 3. Additional Pulled Details (Clean bullet list when extra details exist) */}
                          <Show when={(group.keyDetails || []).length > 0}>
                            <ul class="osint-Research__groupKeyDetails">
                              <For each={group.keyDetails.slice(0, 6)}>
                                {detail => <li>{detail}</li>}
                              </For>
                            </ul>
                          </Show>

                          {/* 4. Clean Card Footer: Sources Summarized + Optional Expandable Raw Source Records */}
                          <div class="osint-Research__groupFooterBar" onClick={e => e.stopPropagation()}>
                            <div class="osint-Research__groupFooterSources">
                              <span>
                                📚 Verified across <strong>{group.entryCount || group.entries?.length || 1}</strong> public record{(group.entryCount || 1) === 1 ? '' : 's'}
                                <Show when={(group.sources || []).length > 0}>
                                  {': '}{group.sources.slice(0, 3).join(' · ')}
                                </Show>
                              </span>
                            </div>

                            <Show when={(group.entries || []).length > 0}>
                              <button
                                type="button"
                                class="osint-Research__groupExpandBtn"
                                onClick={e => toggleGroupExpanded(group.id, e)}
                              >
                                {isExpanded()
                                  ? 'Hide Source Breakdown ▲'
                                  : `View Source Breakdown (${group.entries.length}) ▼`}
                              </button>
                            </Show>
                          </div>

                          {/* Optional Expanded Source Breakdown (collapsed by default so cards stay clean & beginner-friendly) */}
                          <Show when={isExpanded()}>
                            <div class="osint-Research__groupEntriesWrap">
                              <div class="osint-Research__groupEntriesList">
                                <For each={group.entries || []}>
                                  {entry => (
                                    <div
                                      class="osint-Research__simplifiedEntryCard"
                                      onClick={e => {
                                        e.stopPropagation();
                                        selectPotentialMatchAsTarget(group, entry);
                                      }}
                                      title="Click to target this specific record on the dashboard"
                                    >
                                      <div class="osint-Research__simplifiedEntryTop">
                                        <div class="osint-Research__simplifiedEntryHeadline">
                                          <span class="osint-Research__simplifiedEntryCategory">
                                            {entry.friendlyCategory || '🔎 Record'}
                                          </span>
                                          <strong>{entry.simplified?.headline || entry.title}</strong>
                                        </div>
                                        <div class="osint-Research__simplifiedEntryRight" onClick={e => e.stopPropagation()}>
                                          <span class="osint-Research__simplifiedSourceTag">
                                            🏛️ {entry.provenance?.source || entry.source}
                                          </span>
                                          <button
                                            type="button"
                                            class="osint-Research__portMiniBtn"
                                            onClick={() => selectPotentialMatchAsTarget(group, entry)}
                                          >
                                            🎯 Target Record
                                          </button>
                                        </div>
                                      </div>

                                      <Show when={(entry.simplified?.facts || []).length > 0}>
                                        <div class="osint-Research__entryInlineFacts">
                                          <For each={entry.simplified.facts}>
                                            {efact => (
                                              <span class="osint-Research__entryFactChip">
                                                {efact.icon} <strong>{efact.label}:</strong> {efact.value}
                                              </span>
                                            )}
                                          </For>
                                        </div>
                                      </Show>

                                      <Show
                                        when={(entry.simplified?.inlineContent || []).length > 0}
                                        fallback={<p class="osint-Research__cardSummary">{entry.summary}</p>}
                                      >
                                        <ul class="osint-Research__entryInlineBullets">
                                          <For each={entry.simplified.inlineContent}>
                                            {line => <li>{line}</li>}
                                          </For>
                                        </ul>
                                      </Show>
                                    </div>
                                  )}
                                </For>
                              </div>
                            </div>
                          </Show>
                        </article>
                      );
                    }}
                  </For>
                </Show>
              </div>
            </div>
          )}
        </Show>
      </Show>

      {/* ═══════════════════════════════════════════════════════════════════
          PRO MODE (11 ENTITY MODES, PIVOTING, CASE BOARD, FILE LAB, COPILOT)
         ═══════════════════════════════════════════════════════════════════ */}
      <Show when={uiMode() === 'pro'}>
        {/* 2. "Find By" Mode Selector Strip (11 Entity Modes) */}
        <div class="osint-Research__modes" role="tablist" aria-label="Find By Mode">
          <For each={modes()}>
            {mode => (
              <button
                type="button"
                role="tab"
                aria-selected={selectedMode() === mode.id}
                class="osint-Research__modeBtn"
                classList={{ 'is-active': selectedMode() === mode.id }}
                onClick={() => {
                  setSelectedMode(mode.id);
                  setError(null);
                }}
                title={mode.description}
              >
                {mode.shortLabel || mode.label}
              </button>
            )}
          </For>
        </div>

        {/* 3. Unified Search Bar + Auto-Detect Badge + Quick Example Chips */}
        <div class="osint-Research__searchBox">
          <div class="osint-Research__searchRow">
            <div class="osint-Research__inputWrap">
              <label class="osint-Research__srOnly" for="osint-find-by-input">
                Target to investigate
              </label>
              <input
                id="osint-find-by-input"
                type="search"
                enterkeyhint="search"
                autocomplete="off"
                spellcheck={false}
                class="osint-Research__input"
                placeholder={activeModeObj()?.placeholder || 'Enter target…'}
                value={targetInput()}
                onInput={e => setTargetInput(e.currentTarget.value)}
                onKeyDown={handleSearchKeyDown}
              />
              <Show when={detectedModeHint()}>
                <span class="osint-Research__detectedPill" title="Auto-detected target classification">
                  → {detectedModeHint().shortLabel}
                </span>
              </Show>
            </div>
            <button
              type="button"
              class="osint-Research__submitBtn"
              disabled={loading()}
              onClick={() => runSearch()}
            >
              {loading() ? 'Sweeping…' : 'Investigate'}
            </button>
          </div>

          {/* Multi-Hop Investigation Trail Breadcrumbs */}
          <Show when={trail().length > 0}>
            <div class="osint-Research__trailBar">
              <span class="osint-Research__trailLabel">🔗 Trail:</span>
              <For each={trail()}>
                {(hop, idx) => (
                  <>
                    <Show when={idx() > 0}>
                      <span class="osint-Research__trailArrow">→</span>
                    </Show>
                    <button
                      type="button"
                      class="osint-Research__trailHop"
                      classList={{ 'is-current': result()?.target === hop.target && result()?.mode === hop.mode }}
                      onClick={() => runSearch(hop.target, hop.mode)}
                    >
                      {hop.target} <small>({hop.count})</small>
                    </button>
                  </>
                )}
              </For>
              <button
                type="button"
                class="osint-Research__trailClear"
                onClick={() => setTrail([])}
                title="Clear investigation trail"
              >
                Clear
              </button>
            </div>
          </Show>

          <div class="osint-Research__modeFooter">
            <span class="osint-Research__modeDesc">{activeModeObj()?.description}</span>
            <Show when={(activeModeObj()?.examples || []).length > 0}>
              <div class="osint-Research__examples">
                <span class="osint-Research__examplesLabel">Try:</span>
                <For each={activeModeObj().examples}>
                  {ex => (
                    <button
                      type="button"
                      class="osint-Research__exampleChip"
                      onClick={() => runSearch(ex, selectedMode())}
                    >
                      {ex}
                    </button>
                  )}
                </For>
              </div>
            </Show>
          </div>
        </div>

        {/* VIEW 1: INTELLIGENCE FEED */}
        <Show when={activeView() === 'feed'}>
          <Show
            when={result()}
            fallback={
              <div class="osint-Research__emptyState">
                <div class="osint-Research__emptyGrid">
                  <div class="osint-Research__capCard">
                    <div class="osint-Research__capTitle">👤 Identity & Commit Mining</div>
                    <p>Sweeps local Redfur DB + 24 developer, social, fediverse, and Wikipedia APIs, plus deep GitHub PushEvent commit-patch email mining & Gists.</p>
                  </div>
                  <div class="osint-Research__capCard">
                    <div class="osint-Research__capTitle">🏛️ Corporate, Academic & Geo</div>
                    <p>Queries GLEIF Global LEI corporate filings, OpenAlex researchers/ORCID & ROR institutions, Crossref DOIs, Wikidata, OpenStreetMap & Archive.org.</p>
                  </div>
                  <div class="osint-Research__capCard">
                    <div class="osint-Research__capTitle">🌐 Domain, TLS & BGP Netblocks</div>
                    <p>Local DNS suite, direct port 443 X.509 certificate socket inspection, RDAP, RIPE Stat BGP prefixes, urlscan.io, crt.sh & Shodan InternetDB.</p>
                  </div>
                  <div class="osint-Research__capCard">
                    <div class="osint-Research__capTitle">⛓️ Web3, 🛡️ CVEs, 📞 Phone & ✈️ ADS-B</div>
                    <p>Ethereum RPC & ENS social reverse lookup, Bitcoin Mempool ledger, MITRE CVE / Google OSV advisories, local ITU/NANPA phone plan, OUI & OpenSky.</p>
                  </div>
                </div>
              </div>
            }
          >
            {/* Unified Dossier Profile Banner */}
            <Show when={result().profileCard}>
              <section class="osint-Research__dossierCard">
                <Show when={result().profileCard.avatar}>
                  <img
                    src={result().profileCard.avatar}
                    alt={result().profileCard.title}
                    class="osint-Research__dossierAvatar"
                    loading="lazy"
                  />
                </Show>
                <div class="osint-Research__dossierBody">
                  <div class="osint-Research__dossierTop">
                    <div>
                      <div class="osint-Research__dossierKind">{result().profileCard.kind}</div>
                      <h4 class="osint-Research__dossierTitle">{result().profileCard.title}</h4>
                      <div class="osint-Research__dossierSub">{result().profileCard.subtitle}</div>
                    </div>
                    <div class="osint-Research__dossierActions">
                      <span class="osint-Research__telemetryPill">
                        {result().sourcesChecked || 1} sources · {result().elapsedMs || 0}ms
                      </span>
                      <button
                        type="button"
                        class="osint-Research__askFissalBtn"
                        onClick={() => askFissalCopilot()}
                        title="Have Fissal analyze these findings and autonomously run follow-up OSINT tools"
                      >
                        🧠 Deep Brief
                      </button>
                    </div>
                  </div>
                  <p class="osint-Research__dossierSummary">{result().profileCard.summary}</p>
                  <div class="osint-Research__dossierBadges">
                    <For each={result().profileCard.badges || []}>
                      {b => <span class="osint-Research__badge">{b}</span>}
                    </For>
                  </div>
                </div>
              </section>
            </Show>

            {/* Discovered Pivot Leads Strip (One-Click Multi-Hop Pivoting) */}
            <Show when={pivots().length > 0}>
              <div class="osint-Research__pivotsStrip">
                <span class="osint-Research__pivotsLabel">🎯 Pivot Leads:</span>
                <div class="osint-Research__pivotsList">
                  <For each={pivots()}>
                    {piv => (
                      <button
                        type="button"
                        class="osint-Research__pivotChip"
                        onClick={() => runSearch(piv.target, piv.mode)}
                        title={`Pivot investigation to ${piv.target} (${piv.mode})`}
                      >
                        {piv.label}
                      </button>
                    )}
                  </For>
                </div>
              </div>
            </Show>

            {/* Category Filter Pills + Instant Filter Input */}
            <div class="osint-Research__filterBar">
              <div class="osint-Research__filterPills">
                <For each={filters()}>
                  {f => {
                    const count = () => categoryCounts()[f.id] || 0;
                    return (
                      <Show when={f.id === 'all' || count() > 0}>
                        <button
                          type="button"
                          class="osint-Research__filterPill"
                          classList={{ 'is-active': activeFilter() === f.id }}
                          onClick={() => setActiveFilter(f.id)}
                        >
                          {f.label} <span class="osint-Research__filterCount">{count()}</span>
                        </button>
                      </Show>
                    );
                  }}
                </For>
              </div>
              <input
                type="search"
                class="osint-Research__subFilter"
                placeholder="Filter evidence…"
                value={filterQuery()}
                onInput={e => setFilterQuery(e.currentTarget.value)}
              />
            </div>

            {/* Evidence Cards List */}
            <div class="osint-Research__findingsList">
              <Show
                when={filteredFindings().length > 0}
                fallback={<div class="osint-Research__noMatches">No evidence cards match the current filter.</div>}
              >
                <For each={filteredFindings()}>
                  {item => {
                    const conf = CONFIDENCE_LABELS[item.confidence] || CONFIDENCE_LABELS['direct-api'];
                    return (
                      <article class="osint-Research__card osint-Research__card--clickable" onClick={() => openSubjectModal(item)}>
                        <div class="osint-Research__cardHeader">
                          <div class="osint-Research__cardTitleRow">
                            <span class="osint-Research__cardIcon">{TYPE_ICONS[item.type] || '🔎'}</span>
                            <span class="osint-Research__cardTitle">{item.title}</span>
                          </div>
                          <div class="osint-Research__cardRight" onClick={e => e.stopPropagation()}>
                            <span class={`osint-Research__conf ${conf.cls}`}>{conf.label}</span>
                            <span class="osint-Research__sourceTag">{item.source}</span>
                            <button
                              type="button"
                              class="osint-Research__portMiniBtn"
                              onClick={() => portRecordToWidget(item, false)}
                              title="Port this record's data into the widget fields"
                            >
                              📥 Port Data
                            </button>
                            <button
                              type="button"
                              class="osint-Research__pinBtn"
                              classList={{ 'is-pinned': isPinned(item.id) }}
                              onClick={() => togglePin(item)}
                              title={isPinned(item.id) ? 'Remove from Case Board' : 'Pin to Case Board Dossier'}
                            >
                              {isPinned(item.id) ? '📌 Pinned' : '📌 Pin'}
                            </button>
                            <button
                              type="button"
                              class="osint-Research__copyBtn"
                              onClick={() => copyFinding(item)}
                              title="Copy finding to clipboard"
                            >
                              {copiedId() === item.id ? '✓' : '📋'}
                            </button>
                          </div>
                        </div>

                        <p class="osint-Research__cardSummary">{item.summary}</p>

                        <div class="osint-Research__cardFooter" onClick={e => e.stopPropagation()}>
                          <Show when={(item.meta || []).length > 0}>
                            <div class="osint-Research__cardMeta">
                              <For each={item.meta}>
                                {m => <span class="osint-Research__metaChip">{m}</span>}
                              </For>
                            </div>
                          </Show>
                          <Show when={(item.pivots || []).length > 0}>
                            <div class="osint-Research__cardPivots">
                              <For each={item.pivots}>
                                {p => (
                                  <button
                                    type="button"
                                    class="osint-Research__cardPivotBtn"
                                    onClick={() => runSearch(p.target, p.mode)}
                                    title={`Pivot to ${p.target} (${p.mode})`}
                                  >
                                    Pivot: {p.label}
                                  </button>
                                )}
                              </For>
                            </div>
                          </Show>
                        </div>
                      </article>
                    );
                  }}
                </For>
              </Show>
            </div>

            {/* Direct Target Verification Strip */}
            <Show when={(result().references || []).length > 0}>
              <footer class="osint-Research__refsBar">
                <span class="osint-Research__refsLabel">Direct Verification:</span>
                <div class="osint-Research__refsLinks">
                  <For each={result().references}>
                    {ref => (
                      <a
                        href={ref.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="osint-Research__refChip"
                      >
                        {ref.label} ↗
                      </a>
                    )}
                  </For>
                </div>
              </footer>
            </Show>
          </Show>
        </Show>

        {/* VIEW 2: CASE BOARD */}
        <Show when={activeView() === 'caseboard'}>
          <div class="osint-Research__caseboard">
            <div class="osint-Research__caseHeader">
              <div>
                <h4 class="osint-Research__caseTitle">📌 Case Board · Pinned Evidence Dossier</h4>
                <p class="osint-Research__caseSub">
                  Pin findings across multiple searches and pivots to build a structured intelligence report.
                </p>
              </div>
              <div class="osint-Research__caseActions">
                <button
                  type="button"
                  class="osint-Research__exportBtn"
                  disabled={pinnedItems().length === 0 && !caseNotes().trim()}
                  onClick={() => exportCaseBoard('md')}
                >
                  ⬇ Export Markdown (.md)
                </button>
                <button
                  type="button"
                  class="osint-Research__exportBtn"
                  disabled={pinnedItems().length === 0 && !caseNotes().trim()}
                  onClick={() => exportCaseBoard('json')}
                >
                  ⬇ Export JSON (.json)
                </button>
                <Show when={pinnedItems().length > 0}>
                  <button
                    type="button"
                    class="osint-Research__clearPinsBtn"
                    onClick={() => setPinnedItems([])}
                  >
                    Clear All
                  </button>
                </Show>
              </div>
            </div>

            <textarea
              class="osint-Research__caseNotes"
              rows={3}
              placeholder="Add investigator notes, hypotheses, or timeline observations for this dossier…"
              value={caseNotes()}
              onInput={e => setCaseNotes(e.currentTarget.value)}
            />

            <Show
              when={pinnedItems().length > 0}
              fallback={
                <div class="osint-Research__noMatches">
                  No evidence cards pinned yet. Click <strong>📌 Pin</strong> on any card in the Intelligence Feed to add it to your dossier.
                </div>
              }
            >
              <div class="osint-Research__findingsList">
                <For each={pinnedItems()}>
                  {item => (
                    <article class="osint-Research__card">
                      <div class="osint-Research__cardHeader">
                        <div class="osint-Research__cardTitleRow">
                          <span class="osint-Research__cardIcon">{TYPE_ICONS[item.type] || '📌'}</span>
                          <span class="osint-Research__cardTitle">{item.title}</span>
                        </div>
                        <div class="osint-Research__cardRight">
                          <span class="osint-Research__sourceTag">Target: {item.pinnedTarget}</span>
                          <span class="osint-Research__sourceTag">{item.source}</span>
                          <button
                            type="button"
                            class="osint-Research__pinBtn is-pinned"
                            onClick={() => togglePin(item)}
                          >
                            ✕ Unpin
                          </button>
                        </div>
                      </div>
                      <p class="osint-Research__cardSummary">{item.summary}</p>
                    </article>
                  )}
                </For>
              </div>
            </Show>
          </div>
        </Show>

        {/* VIEW 3: LOCAL FILE LAB */}
        <Show when={activeView() === 'filelab'}>
          <div class="osint-Research__fileLab">
            <div class="osint-Research__fileLabHeader">
              <div>
                <h4 class="osint-Research__caseTitle">🧬 Sovereign Local File Lab (100% Client-Side)</h4>
                <p class="osint-Research__caseSub">
                  Inspect any file locally in your browser. Computes cryptographic SHA-256 / SHA-1 hashes, magic bytes, image dimensions, and extracts embedded EXIF/ASCII URLs & emails without uploading a single byte.
                </p>
              </div>
              <label class="osint-Research__fileUploadBtn">
                {fileAnalyzing() ? 'Analyzing…' : '📂 Select File to Inspect'}
                <input
                  type="file"
                  style={{ display: 'none' }}
                  onChange={e => inspectLocalFile(e.currentTarget.files?.[0])}
                />
              </label>
            </div>

            <Show
              when={fileReport()}
              fallback={
                <div class="osint-Research__noMatches">
                  Select any image, PDF, archive, or binary above to compute local SHA-256/SHA-1 hashes and extract embedded metadata leads.
                </div>
              }
            >
              {rep => (
                <div class="osint-Research__fileReport">
                  <div class="osint-Research__card">
                    <div class="osint-Research__cardHeader">
                      <span class="osint-Research__cardTitle">📄 {rep().name}</span>
                      <span class="osint-Research__conf osint-Research__conf--local">⚡ 100% Browser Local</span>
                    </div>
                    <p class="osint-Research__cardSummary">
                      MIME: <strong>{rep().mimeType}</strong> · Size: <strong>{rep().sizeBytes.toLocaleString()} bytes</strong>
                      {rep().dimensions ? ` · Dimensions: ${rep().dimensions}` : ''}
                      {rep().lastModified ? ` · Modified: ${rep().lastModified}` : ''}
                    </p>
                    <div class="osint-Research__hashBox">
                      <div><strong>SHA-256:</strong> <code>{rep().sha256}</code></div>
                      <div><strong>SHA-1:</strong> <code>{rep().sha1}</code></div>
                      <div><strong>Magic Header (Hex):</strong> <code>{rep().magicHex}</code></div>
                    </div>
                    <Show when={rep().exifHints.length > 0}>
                      <div class="osint-Research__cardMeta">
                        <span class="osint-Research__pivotsLabel">Header / EXIF Strings:</span>
                        <For each={rep().exifHints}>
                          {h => <span class="osint-Research__metaChip">{h}</span>}
                        </For>
                      </div>
                    </Show>
                    <Show when={rep().emails.length > 0 || rep().urls.length > 0}>
                      <div class="osint-Research__cardPivots" style={{ 'margin-top': '8px' }}>
                        <span class="osint-Research__pivotsLabel">Embedded Leads:</span>
                        <For each={rep().emails}>
                          {em => (
                            <button
                              type="button"
                              class="osint-Research__cardPivotBtn"
                              onClick={() => runSearch(em, 'email')}
                            >
                              Pivot: ✉️ {em}
                            </button>
                          )}
                        </For>
                        <For each={rep().urls}>
                          {u => (
                            <button
                              type="button"
                              class="osint-Research__cardPivotBtn"
                              onClick={() => runSearch(u, 'domain')}
                            >
                              Pivot: 🌐 {u.slice(0, 45)}
                            </button>
                          )}
                        </For>
                      </div>
                    </Show>
                  </div>
                </div>
              )}
            </Show>
          </div>
        </Show>

        {/* VIEW 4: FISSAL OSINT CO-PILOT */}
        <Show when={activeView() === 'fissal'}>
          <div class="osint-Research__copilot">
            <div class="osint-Research__copilotHeader">
              <div>
                <div class="osint-Research__kicker">CLOCKWORK ALFIQ ANALYST · AUTONOMOUS OSINT TOOL CALLING</div>
                <h4 class="osint-Research__copilotTitle">
                  🧠 Fissal OSINT Co-Pilot
                  <Show when={result()?.target}>
                    <span class="osint-Research__copilotTarget">Target: {result().target}</span>
                  </Show>
                </h4>
              </div>
              <div class="osint-Research__copilotPresets">
                <button
                  type="button"
                  class="osint-Research__presetBtn"
                  disabled={fissalStreaming()}
                  onClick={() => askFissalCopilot('Correlate all current findings, identify high-confidence attribution links, and call your OSINT tools on the top pivot lead.')}
                >
                  🔗 Correlate & Auto-Pivot
                </button>
                <button
                  type="button"
                  class="osint-Research__presetBtn"
                  disabled={fissalStreaming()}
                  onClick={() => askFissalCopilot('Search public corporate/academic records and mine GitHub commits if a handle is present.')}
                >
                  🏛️ Deep Public Records Sweep
                </button>
                <button
                  type="button"
                  class="osint-Research__presetBtn"
                  disabled={fissalStreaming()}
                  onClick={() => askFissalCopilot('Evaluate the infrastructure, DNS/TLS security posture, and exposed services for this target.')}
                >
                  🛡️ Security Posture Audit
                </button>
              </div>
            </div>

            <div class="osint-Research__copilotPromptRow">
              <input
                type="text"
                class="osint-Research__copilotInput"
                placeholder="Ask Fissal to investigate a lead, pivot on an email/domain, or query public registries…"
                value={fissalPrompt()}
                onInput={e => setFissalPrompt(e.currentTarget.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.isComposing) {
                    e.preventDefault();
                    askFissalCopilot();
                  }
                }}
              />
              <button
                type="button"
                class="osint-Research__submitBtn"
                disabled={fissalStreaming()}
                onClick={() => askFissalCopilot()}
              >
                {fissalStreaming() ? 'Investigating…' : 'Dispatch Fissal'}
              </button>
            </div>

            <Show when={fissalToolsUsed().length > 0}>
              <div class="osint-Research__toolCallsBar">
                <span class="osint-Research__pivotsLabel">🔧 Fissal Tool Calls:</span>
                <For each={fissalToolsUsed()}>
                  {tc => (
                    <span class="osint-Research__toolCallBadge" classList={{ 'is-running': tc.status === 'running' }}>
                      {tc.status === 'running' ? '⏳' : '✓'} <strong>{tc.name}</strong>
                      ({tc.args?.target || tc.args?.username || tc.args?.query || tc.args?.address_or_ens || tc.args?.vuln_id || 'auto'})
                      {tc.status === 'done' ? ` → +${tc.count} records merged` : '…'}
                    </span>
                  )}
                </For>
              </div>
            </Show>

            <Show when={fissalError()}>
              <div class="osint-Research__error">{fissalError()}</div>
            </Show>

            <div class="osint-Research__copilotOutput">
              <Show
                when={fissalResponse() || fissalStreaming()}
                fallback={
                  <div class="osint-Research__copilotEmpty">
                    Fissal is equipped with 5 live OSINT tools (<code>osint_find_by</code>, <code>osint_mine_github_user</code>, <code>osint_search_public_records</code>, <code>osint_inspect_crypto</code>, <code>osint_lookup_cve</code>). Ask a question above or click a preset—any public records Fissal discovers are automatically merged into your Intelligence Feed.
                  </div>
                }
              >
                <div class="osint-Research__copilotText">
                  {fissalResponse()}
                  <Show when={fissalStreaming()}>
                    <span class="osint-Research__cursor">▊</span>
                  </Show>
                </div>
              </Show>
            </div>
          </div>
        </Show>
      </Show>

      {/* ═══════════════════════════════════════════════════════════════════
          SUBJECT DOSSIER POP-UP MODAL + EMBEDDED FISSAL CHAT + PORT TO WIDGET
         ═══════════════════════════════════════════════════════════════════ */}
      <Show when={selectedSubject()}>
        {subj => {
          const fields = () => subj().extractedFields || {};
          return (
            <div
              class="osint-Research__modalOverlay"
              role="dialog"
              aria-modal="true"
              aria-label="Subject Intelligence Dossier"
              onClick={() => setSelectedSubject(null)}
            >
              <div class="osint-Research__modal" onClick={e => e.stopPropagation()}>
                {/* Modal Header */}
                <header class="osint-Research__modalHeader">
                  <div class="osint-Research__modalTitleGroup">
                    <Show when={subj().avatar}>
                      <img src={subj().avatar} alt="" class="osint-Research__modalAvatar" />
                    </Show>
                    <div>
                      <div class="osint-Research__modalKicker">
                        {subj().isBestMatch ? '🏆 #1 BEST MATCH SUBJECT DOSSIER' : 'SUBJECT INTELLIGENCE DOSSIER'}
                        {' · '}
                        {subj().friendlyCategory || subj().source}
                      </div>
                      <h4 class="osint-Research__modalTitle">{subj().title}</h4>
                    </div>
                  </div>

                  <div class="osint-Research__modalActions">
                    <button
                      type="button"
                      class="osint-Research__deepPullBtn"
                      disabled={deepPullBusy()}
                      onClick={() => runDeepPullForSubject(subj())}
                      title="Pull all available public records, probe cross-referenced handles, and load the OSINT Framework tree for this subject"
                    >
                      {deepPullBusy() ? '⏳ Pulling Public Dossier…' : '⚡ Pull All Public Records & OSINT'}
                    </button>
                    <button
                      type="button"
                      class="osint-Research__portBtn"
                      onClick={() => portRecordToWidget(subj(), true)}
                      title="Fill all widget input boxes with the specific data found on this record"
                    >
                      📥 Port This Data to Widget
                    </button>
                    <button
                      type="button"
                      class="osint-Research__pinBtn"
                      classList={{ 'is-pinned': isPinned(subj().id) }}
                      onClick={() => togglePin(subj())}
                    >
                      {isPinned(subj().id) ? '📌 Pinned' : '📌 Pin to Case Board'}
                    </button>
                    <button
                      type="button"
                      class="osint-Research__modalClose"
                      onClick={() => setSelectedSubject(null)}
                      aria-label="Close Subject Dossier"
                    >
                      ✕
                    </button>
                  </div>
                </header>

                {/* Modal Two-Column Body: Left = All Subject Data, Right = Dig Further with Fissal */}
                <div class="osint-Research__modalBody">
                  {/* Left Column: Complete Subject Information, Provenance, Extracted Fields & Links */}
                  <div class="osint-Research__modalIntel">
                    {/* 1. Full Summary & Clue Corroboration */}
                    <div class="osint-Research__modalSection">
                      <div class="osint-Research__modalSectionTitle">📋 Subject Overview & Why It Matched</div>
                      <p class="osint-Research__modalSummary">{subj().summary}</p>
                      <Show when={(subj().corroborations || []).length > 0}>
                        <div class="osint-Research__cardMeta" style={{ 'margin-top': '8px' }}>
                          <For each={subj().corroborations}>
                            {c => <span class="osint-Research__metaChip">✓ {c}</span>}
                          </For>
                        </div>
                      </Show>
                    </div>

                    {/* 2. Discovered Structured Record Fields (Ready to Port) */}
                    <div class="osint-Research__modalSection">
                      <div class="osint-Research__modalSectionTitle">
                        <span>🗂️ Specific Record Data (Click "Port This Data to Widget" to Fill Fields)</span>
                      </div>
                      <div class="osint-Research__fieldGrid">
                        <div class="osint-Research__fieldItem">
                          <span class="osint-Research__fieldKey">👤 Name / Entity</span>
                          <span class="osint-Research__fieldVal">
                            {fields().name || simpleResult()?.subjectProfile?.fullName || 'Not listed on record'}
                            <Show when={simpleResult()?.subjectProfile?.nameParts?.initialsFormatted}>
                              {' '}({simpleResult().subjectProfile.nameParts.initialsFormatted})
                            </Show>
                          </span>
                        </div>
                        <div class="osint-Research__fieldItem">
                          <span class="osint-Research__fieldKey">📍 Address / State</span>
                          <span class="osint-Research__fieldVal">
                            {fields().location || simpleResult()?.subjectProfile?.addresses?.[0] || 'Not listed on record'}
                          </span>
                        </div>
                        <div class="osint-Research__fieldItem">
                          <span class="osint-Research__fieldKey">🎂 Age / Birth Year</span>
                          <span class="osint-Research__fieldVal">
                            {fields().age || simpleResult()?.subjectProfile?.age || 'Not listed on record'}
                          </span>
                        </div>
                        <div class="osint-Research__fieldItem">
                          <span class="osint-Research__fieldKey">🏷️ Primary Handle</span>
                          <span class="osint-Research__fieldVal">
                            {fields().username
                              ? `@${fields().username}`
                              : simpleResult()?.subjectProfile?.crossReferencedHandles?.[0]?.handle
                              ? `@${simpleResult().subjectProfile.crossReferencedHandles[0].handle}`
                              : 'Not listed on record'}
                          </span>
                        </div>
                        <div class="osint-Research__fieldItem">
                          <span class="osint-Research__fieldKey">✉️ Email Address</span>
                          <span class="osint-Research__fieldVal">
                            {fields().email || simpleResult()?.subjectProfile?.emails?.[0] || 'Not listed on record'}
                          </span>
                        </div>
                        <div class="osint-Research__fieldItem">
                          <span class="osint-Research__fieldKey">📞 Phone Number</span>
                          <span class="osint-Research__fieldVal">
                            {fields().phone || simpleResult()?.subjectProfile?.phones?.[0] || 'Not listed on record'}
                          </span>
                        </div>
                        <div class="osint-Research__fieldItem">
                          <span class="osint-Research__fieldKey">🌐 Website / Profile</span>
                          <span class="osint-Research__fieldVal">{fields().website || subj().url || 'Not listed on record'}</span>
                        </div>
                        <div class="osint-Research__fieldItem">
                          <span class="osint-Research__fieldKey">🏢 Organization</span>
                          <span class="osint-Research__fieldVal">{fields().organization || 'Not listed on record'}</span>
                        </div>
                      </div>
                    </div>

                    {/* 3. Cross-Referenced Usernames & Potential Socials for This Subject */}
                    <Show when={(simpleResult()?.subjectProfile?.crossReferencedHandles || []).length > 0}>
                      <div class="osint-Research__modalSection">
                        <div class="osint-Research__modalSectionTitle">
                          🧬 Cross-Referenced Usernames & Related Handles ({simpleResult().subjectProfile.crossReferencedHandles.length})
                        </div>
                        <div class="osint-Research__xrefGrid">
                          <For each={simpleResult().subjectProfile.crossReferencedHandles.slice(0, 10)}>
                            {h => (
                              <div
                                class="osint-Research__xrefCard"
                                classList={{ 'is-verified': h.verified || (h.platforms || []).length > 0 }}
                              >
                                <div class="osint-Research__xrefCardTop">
                                  <span class="osint-Research__xrefHandle">@{h.handle}</span>
                                  <button
                                    type="button"
                                    class="osint-Research__xrefScanBtn"
                                    onClick={() => {
                                      setSelectedSubject(null);
                                      scanCrossReferencedHandle(h.handle);
                                    }}
                                  >
                                    🔎 Scan
                                  </button>
                                </div>
                                <div class="osint-Research__xrefReason">{h.derivation}</div>
                                <Show when={(h.platforms || []).length > 0}>
                                  <div class="osint-Research__xrefHits">
                                    <For each={h.platforms}>
                                      {hit => (
                                        <a
                                          href={hit.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          class="osint-Research__xrefHitChip"
                                        >
                                          {hit.icon || '✓'} {hit.platform} ↗
                                        </a>
                                      )}
                                    </For>
                                  </div>
                                </Show>
                              </div>
                            )}
                          </For>
                        </div>
                      </div>
                    </Show>

                    {/* 4. Where the Data Was Pulled From (Provenance) */}
                    <div class="osint-Research__modalSection">
                      <div class="osint-Research__modalSectionTitle">🏛️ Where This Data Was Pulled From</div>
                      <div class="osint-Research__provenanceBox">
                        <div><strong>Source Registry / API:</strong> {subj().provenance?.source || subj().source}</div>
                        <div><strong>Discovery Path:</strong> {subj().provenance?.pulledVia || subj().matchedVia || 'Direct OSINT Collector'}</div>
                        <div><strong>Verification Confidence:</strong> {subj().provenance?.confidence || subj().confidence || 'direct-api'}</div>
                        <Show when={subj().url}>
                          <div>
                            <strong>Source Record URL:</strong>{' '}
                            <a href={subj().url} target="_blank" rel="noopener noreferrer" class="osint-Research__cardLink">
                              {subj().url} ↗
                            </a>
                          </div>
                        </Show>
                      </div>
                    </div>

                    {/* 5. Attached Links (Socials, Phone, Email, Website, Map) */}
                    <Show when={(subj().attachedLinks || []).length > 0}>
                      <div class="osint-Research__modalSection">
                        <div class="osint-Research__modalSectionTitle">🔗 Attached Socials, Phone, Email & Map Links</div>
                        <div class="osint-Research__attachedLinksRow">
                          <For each={subj().attachedLinks}>
                            {link => (
                              <a
                                href={link.url}
                                target={link.url.startsWith('tel:') || link.url.startsWith('mailto:') ? undefined : '_blank'}
                                rel="noopener noreferrer"
                                class="osint-Research__attachedLinkChip"
                              >
                                {link.icon} {link.label} ↗
                              </a>
                            )}
                          </For>
                        </div>
                      </div>
                    </Show>

                    {/* 6. OSINT Framework & Public Records Links Inside Modal */}
                    <Show when={Array.isArray(simpleResult()?.osintFramework) && simpleResult().osintFramework.length > 0}>
                      <div class="osint-Research__modalSection">
                        <div class="osint-Research__modalSectionTitle">
                          🧭 OSINT Framework & Public Records Deep Links (Age, Phone, Address, Voter, Deeds, Dorks)
                        </div>
                        <div class="osint-Research__frameworkGrid">
                          <For each={simpleResult().osintFramework}>
                            {cat => (
                              <div class="osint-Research__frameworkCat">
                                <div class="osint-Research__frameworkCatTitle">
                                  {cat.title}
                                </div>
                                <div class="osint-Research__frameworkLinks">
                                  <For each={cat.items || []}>
                                    {item => (
                                      <a
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="osint-Research__frameworkLink"
                                        title={item.desc}
                                      >
                                        <span>{item.name} ↗</span>
                                        <small>{item.desc}</small>
                                      </a>
                                    )}
                                  </For>
                                </div>
                              </div>
                            )}
                          </For>
                        </div>
                      </div>
                    </Show>

                    {/* 7. Corroborating / Related Records for This Subject */}
                    <Show when={(subj().relatedFindings || []).length > 0}>
                      <div class="osint-Research__modalSection">
                        <div class="osint-Research__modalSectionTitle">
                          🔎 Related Records for This Subject ({subj().relatedFindings.length})
                        </div>
                        <div class="osint-Research__relatedList">
                          <For each={subj().relatedFindings}>
                            {rel => (
                              <div class="osint-Research__relatedCard">
                                <div class="osint-Research__relatedHeader">
                                  <strong>{rel.title}</strong>
                                  <span class="osint-Research__sourceTag">{rel.source}</span>
                                </div>
                                <p class="osint-Research__cardSummary">{rel.summary}</p>
                              </div>
                            )}
                          </For>
                        </div>
                      </div>
                    </Show>
                  </div>

                  {/* Right Column: Interactive Chat with Fissal to Dig Further */}
                  <div class="osint-Research__modalChat">
                    <div class="osint-Research__modalChatHeader">
                      <div>
                        <div class="osint-Research__kicker">SUBJECT CO-PILOT · AUTONOMOUS OSINT TOOLS</div>
                        <h5 class="osint-Research__modalChatTitle">💬 Dig Further with Fissal</h5>
                      </div>
                      <div class="osint-Research__modalChatQuick">
                        <button
                          type="button"
                          class="osint-Research__exampleChip"
                          disabled={subjectChatStreaming()}
                          onClick={() =>
                            askSubjectFissal(
                              'Dig deeper into this subject using your OSINT tools. Check for commit emails, additional social handles, or public corporate/academic records.',
                            )
                          }
                        >
                          🔎 Dig Deeper
                        </button>
                        <button
                          type="button"
                          class="osint-Research__exampleChip"
                          disabled={subjectChatStreaming()}
                          onClick={() =>
                            askSubjectFissal(
                              `Verify how strongly this subject matches the location "${simpleLocation() || 'entered'}" and age "${simpleAge() || 'entered'}", and check for any other public links.`,
                            )
                          }
                        >
                          📍 Verify Location & Age
                        </button>
                      </div>
                    </div>

                    <Show when={subjectToolsUsed().length > 0}>
                      <div class="osint-Research__toolCallsBar">
                        <For each={subjectToolsUsed()}>
                          {tc => (
                            <span class="osint-Research__toolCallBadge" classList={{ 'is-running': tc.status === 'running' }}>
                              {tc.status === 'running' ? '⏳' : '✓'} {tc.name}
                              {tc.status === 'done' ? ` (+${tc.count})` : '…'}
                            </span>
                          )}
                        </For>
                      </div>
                    </Show>

                    <div class="osint-Research__modalChatMessages">
                      <For each={subjectChatMessages()}>
                        {msg => (
                          <div
                            class="osint-Research__chatBubble"
                            classList={{
                              'osint-Research__chatBubble--user': msg.role === 'user',
                              'osint-Research__chatBubble--fissal': msg.role === 'fissal',
                            }}
                          >
                            <div class="osint-Research__chatRole">
                              {msg.role === 'user' ? '👤 You' : '🐾 Fissal'}
                            </div>
                            <div class="osint-Research__chatText">
                              {msg.text}
                              <Show when={msg.streaming}>
                                <span class="osint-Research__cursor">▊</span>
                              </Show>
                            </div>
                          </div>
                        )}
                      </For>
                    </div>

                    <div class="osint-Research__modalChatInputRow">
                      <input
                        type="text"
                        class="osint-Research__copilotInput"
                        placeholder="Ask Fissal to dig further into this subject…"
                        value={subjectChatInput()}
                        onInput={e => setSubjectChatInput(e.currentTarget.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && !e.isComposing) {
                            e.preventDefault();
                            askSubjectFissal();
                          }
                        }}
                      />
                      <button
                        type="button"
                        class="osint-Research__submitBtn"
                        disabled={subjectChatStreaming()}
                        onClick={() => askSubjectFissal()}
                      >
                        {subjectChatStreaming() ? 'Digging…' : 'Ask Fissal'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      </Show>
    </div>
    </Show>
  );
}
